import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Save, Trash2, Plus, X, Image as ImageIcon,
  FileText, Video as VideoIcon, Podcast as PodcastIcon,
  BookOpen, Calendar, Settings, Users, Mail,
  Facebook, Instagram, Youtube, Sparkles, Search, Filter, Edit, Edit3, RefreshCw, DollarSign, Target, Brain, GraduationCap
} from 'lucide-react';
import BookingsManager from '@/components/BookingsManager';
import CoursesManagerEnhanced from '@/components/CoursesManagerEnhanced';
import PageSettingsManager from '@/components/PageSettingsManager';
import TestingCenterSettings from '@/components/TestingCenterSettings';
import TestBankManager from '@/components/TestBankManager';
import QuestionBankManager from '@/components/QuestionBankManager';
import PricingManager from '@/components/PricingManager';
import TestCategoriesManager from '@/components/TestCategoriesManager';
import AcupunctureCardManager from '@/components/AcupunctureCardManager';
import AreasManager from '@/components/AreasManager';
import UnsplashImagePicker from '@/components/UnsplashImagePicker';
import MediaPickerModal from '@/components/MediaPickerModal';
import ErnieAiSettings from '@/components/ErnieAiSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'blogs';
  });
  const [loading, setLoading] = useState(false);

  const tabs = [
    { id: 'generation', label: 'Generation', icon: Sparkles },
    { id: 'ernie-ai', label: 'Ernie AI', icon: Brain },
    { id: 'areas', label: 'Areas of Focus', icon: Target },
    { id: 'acupuncture-cards', label: 'Acupuncture Cards', icon: Sparkles },
    { id: 'blogs', label: 'Blog Posts', icon: FileText },
    { id: 'tests', label: 'Test Management', icon: BookOpen },
    { id: 'test-subscriptions', label: 'Test Access', icon: Users },
    { id: 'test-categories', label: 'Test Categories', icon: Target },
    { id: 'videos', label: 'Videos', icon: VideoIcon },
    { id: 'podcasts', label: 'Podcasts', icon: PodcastIcon },
    { id: 'book', label: 'Book', icon: BookOpen },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'classes', label: 'Classes', icon: Brain },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'testing-center', label: 'Testing Center', icon: Settings },
    { id: 'test-bank', label: 'Test Bank', icon: FileText },
    { id: 'question-bank', label: 'Question Bank', icon: Sparkles },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'mailing-list', label: 'Mailing List', icon: Mail },
    { id: 'quotes', label: 'Page Quotes', icon: FileText },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'bookings', label: 'One-on-One Bookings', icon: Calendar },
    { id: 'study-buddy', label: 'Study Buddy', icon: GraduationCap },
    { id: 'about', label: 'About', icon: Users },
    { id: 'group-study', label: 'Group Study', icon: VideoIcon },
    { id: 'hero-images', label: 'Hero Images', icon: ImageIcon },
    { id: 'page-icons', label: 'Page Icons', icon: ImageIcon },
    { id: 'social', label: 'Social Media', icon: Settings },
    { id: 'page-settings', label: 'Page Settings', icon: Settings },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <Layout>
      <section className="py-16 min-h-screen" data-testid="admin-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-7xl retro-title text-center mb-12" data-testid="admin-title">
            Admin Dashboard
          </h1>

          {/* Quick Actions / Main Features */}
          <div className="mb-12">
            <h2 className="text-2xl retro-title text-[#2C1810] mb-6 text-center">Quick Access</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Generation */}
              <button
                onClick={() => setActiveTab('generation')}
                className="cassette-card p-6 hover:scale-105 transition-all duration-300 cursor-pointer group"
              >
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#E85D4D] to-[#E8934D] rounded-2xl opacity-20 group-hover:opacity-30 transition-all"></div>
                    <Sparkles className="w-8 h-8 text-[#E85D4D] relative z-10" />
                  </div>
                  <h3 className="text-xl retro-title text-[#2C1810] mb-2 group-hover:text-[#E85D4D] transition-colors">
                    Generation
                  </h3>
                  <p className="text-[#6B4423] text-sm">
                    AI-powered content creation
                  </p>
                </div>
              </button>

              {/* Users */}
              <button
                onClick={() => setActiveTab('users')}
                className="cassette-card p-6 hover:scale-105 transition-all duration-300 cursor-pointer group"
              >
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#4D9E7C] to-[#E8934D] rounded-2xl opacity-20 group-hover:opacity-30 transition-all"></div>
                    <Users className="w-8 h-8 text-[#4D9E7C] relative z-10" />
                  </div>
                  <h3 className="text-xl retro-title text-[#2C1810] mb-2 group-hover:text-[#4D9E7C] transition-colors">
                    Users
                  </h3>
                  <p className="text-[#6B4423] text-sm">
                    Manage user accounts
                  </p>
                </div>
              </button>

              {/* Test Bank */}
              <button
                onClick={() => setActiveTab('test-bank')}
                className="cassette-card p-6 hover:scale-105 transition-all duration-300 cursor-pointer group"
              >
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#E85D4D] to-[#4D9E7C] rounded-2xl opacity-20 group-hover:opacity-30 transition-all"></div>
                    <FileText className="w-8 h-8 text-[#E85D4D] relative z-10" />
                  </div>
                  <h3 className="text-xl retro-title text-[#2C1810] mb-2 group-hover:text-[#E85D4D] transition-colors">
                    Test Bank
                  </h3>
                  <p className="text-[#6B4423] text-sm">
                    Manage test questions
                  </p>
                </div>
              </button>

              {/* Question Bank */}
              <button
                onClick={() => setActiveTab('question-bank')}
                className="cassette-card p-6 hover:scale-105 transition-all duration-300 cursor-pointer group"
              >
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#E8934D] to-[#4D9E7C] rounded-2xl opacity-20 group-hover:opacity-30 transition-all"></div>
                    <Sparkles className="w-8 h-8 text-[#E8934D] relative z-10" />
                  </div>
                  <h3 className="text-xl retro-title text-[#2C1810] mb-2 group-hover:text-[#E8934D] transition-colors">
                    Question Bank
                  </h3>
                  <p className="text-[#6B4423] text-sm">
                    AI question generation
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center" data-testid="admin-tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`retro-button flex items-center space-x-2 ${
                    activeTab === tab.id ? 'bg-[#6B4423] text-[#F4E4C1]' : ''
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="admin-card">
            {activeTab === 'ernie-ai' && (
              <div className="py-12">
                <ErnieAiSettings />
              </div>
            )}
            
            {activeTab === 'generation' && (
              <div className="py-12">
                <h2 className="text-4xl retro-title text-center mb-8">AI Generation Tools</h2>
                
                <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                  {/* Simple Course Generator */}
                  <div className="cassette-card p-8 text-center">
                    <div className="mb-4">
                      <BookOpen className="w-16 h-16 mx-auto text-[#E85D4D]" />
                    </div>
                    <h3 className="text-2xl retro-title text-[#2C1810] mb-3">Simple Course</h3>
                    <p className="text-[#6B4423] mb-6">
                      Create courses chapter by chapter with AI-generated study materials.
                    </p>
                    <button
                      onClick={() => window.location.href = '/admin/simple-course'}
                      className="retro-button text-lg px-6 py-3 w-full"
                    >
                      Start Simple Course
                    </button>
                  </div>

                  {/* Simple Blog Generator */}
                  <div className="cassette-card p-8 text-center">
                    <div className="mb-4">
                      <Edit3 className="w-16 h-16 mx-auto text-[#4D9E7C]" />
                    </div>
                    <h3 className="text-2xl retro-title text-[#2C1810] mb-3">Simple Blog</h3>
                    <p className="text-[#6B4423] mb-6">
                      Transform raw content into engaging, accessible blog posts instantly.
                    </p>
                    <button
                      onClick={() => window.location.href = '/admin/simple-blog'}
                      className="retro-button text-lg px-6 py-3 w-full"
                    >
                      Create Simple Blog
                    </button>
                  </div>

                  {/* Simple Lesson Generator */}
                  <div className="cassette-card p-8 text-center">
                    <div className="mb-4">
                      <GraduationCap className="w-16 h-16 mx-auto text-emerald-600" />
                    </div>
                    <h3 className="text-2xl retro-title text-[#2C1810] mb-3">Simple Lesson</h3>
                    <p className="text-[#6B4423] mb-6">
                      Create structured lessons with AI-generated study materials for your classes.
                    </p>
                    <button
                      onClick={() => window.location.href = '/admin/simple-lesson'}
                      className="retro-button text-lg px-6 py-3 w-full"
                    >
                      Create Simple Lesson
                    </button>
                  </div>

                  {/* Publication Builder */}
                  <div className="cassette-card p-8 text-center">
                    <div className="mb-4">
                      <FileText className="w-16 h-16 mx-auto text-[#E8934D]" />
                    </div>
                    <h3 className="text-2xl retro-title text-[#2C1810] mb-3">Publication Builder</h3>
                    <p className="text-[#6B4423] mb-6">
                      Create professional publications with AI assistance and citations.
                    </p>
                    <button
                      onClick={() => window.location.href = '/admin/generation'}
                      className="retro-button text-lg px-6 py-3 w-full"
                    >
                      Open Publication Builder
                    </button>
                  </div>
                </div>

                {/* Auto Course Note */}
                <div className="mt-8 text-center space-y-4">
                  <p className="text-[#F4E4C1] text-sm">
                    💡 <strong>Auto Course:</strong> Generate courses from publications via Publication Vault
                  </p>
                  <button
                    onClick={() => window.location.href = '/admin/media-library'}
                    className="retro-button inline-flex items-center gap-2"
                    data-testid="admin-media-library-button"
                  >
                    Media Library
                  </button>
                </div>
              </div>
            )}
            {activeTab === 'areas' && <AreasManager />}
            {activeTab === 'tests' && (
              <div className="cassette-card p-8 text-center">
                <h2 className="text-3xl font-bold text-[#6B4423] mb-4">Test Categorization</h2>
                <p className="text-lg text-gray-700 mb-6">
                  Organize and categorize tests into tiers and sections for the Testing Center.
                </p>
                <button
                  onClick={() => window.location.href = '/admin/test-management'}
                  className="retro-button text-lg px-8 py-3"
                >
                  Manage Test Categories
                </button>
              </div>
            )}
            {activeTab === 'acupuncture-cards' && <AcupunctureCardManager />}
            {activeTab === 'test-subscriptions' && <TestSubscriptionsManager />}
            {activeTab === 'blogs' && <BlogsManager />}
            {activeTab === 'videos' && <VideosManager />}
            {activeTab === 'podcasts' && <PodcastsManager />}
            {activeTab === 'book' && (
              <div className="cassette-card p-8 text-center">
                <h2 className="text-3xl font-bold text-[#6B4423] mb-4">Books</h2>
                <p className="text-lg text-gray-700 mb-6">
                  Manage your public bookstore — add book pages with covers, descriptions, and Buy on Lulu links.
                </p>
                <button
                  onClick={() => window.location.href = '/admin/books'}
                  className="retro-button text-lg px-8 py-3"
                  data-testid="admin-manage-books-button"
                >
                  Manage Books
                </button>
              </div>
            )}
            {activeTab === 'courses' && <CoursesManagerEnhanced />}
            {activeTab === 'classes' && <ClassesManager />}
            {activeTab === 'pricing' && <PricingManager />}
            {activeTab === 'test-categories' && <TestCategoriesManager />}
            {activeTab === 'testing-center' && <TestingCenterSettings />}
            {activeTab === 'test-bank' && <TestBankManager />}
            {activeTab === 'question-bank' && <QuestionBankManager />}
            {activeTab === 'users' && <UsersManager />}
            {activeTab === 'mailing-list' && <MailingListManager />}
            {activeTab === 'quotes' && <QuotesManager />}
            {activeTab === 'schedule' && <ScheduleManager />}
            {activeTab === 'bookings' && <BookingsManager />}
            {activeTab === 'study-buddy' && <StudyBuddyManager />}
            {activeTab === 'about' && <AboutManager />}
            {activeTab === 'group-study' && <GroupStudyManager />}
            {activeTab === 'hero-images' && <HeroImagesManager />}
            {activeTab === 'page-icons' && <PageIconsManager />}
            {activeTab === 'social' && <SocialMediaManager />}
            {activeTab === 'page-settings' && <PageSettingsManager />}
            {activeTab === 'settings' && <SettingsManager />}
          </div>
        </div>
      </section>
    </Layout>
  );
}

// Blogs Manager
function BlogsManager() {
  const [blogs, setBlogs] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    video_url: ''
  });

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      toast.error('Failed to load blogs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await axios.put(`${API}/admin/blogs/${editingBlog.id}`, 
          { ...editingBlog, ...formData },
          { withCredentials: true }
        );
        toast.success('Blog updated!');
      } else {
        await axios.post(`${API}/admin/blogs`, formData, { withCredentials: true });
        toast.success('Blog created!');
      }
      resetForm();
      loadBlogs();
    } catch (error) {
      toast.error('Failed to save blog');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/admin/blogs/${id}`, { withCredentials: true });
      toast.success('Blog deleted!');
      loadBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      image_url: blog.image_url || '',
      video_url: blog.video_url || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ title: '', excerpt: '', content: '', image_url: '', video_url: '' });
    setEditingBlog(null);
    setShowForm(false);
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/media-library/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image_url: `${BACKEND_URL}${response.data.url}` }));
      toast.success('Featured image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload featured image');
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/admin/upload-image`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.image_url;
      });

      const imageUrls = await Promise.all(uploadPromises);
      
      // Add image markdown to content
      const imageMarkdown = imageUrls.map(url => `\n\n![Image](${BACKEND_URL}${url})\n\n`).join('');
      setFormData(prev => ({ 
        ...prev, 
        content: prev.content + imageMarkdown 
      }));
      
      toast.success(`${imageUrls.length} image(s) uploaded!`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const insertImagePlaceholder = () => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + '\n\n[Click "Upload Images" button below to add your Remarkable notes here]\n\n'
    }));
  };

  const handleUnsplashSelect = (url) => {
    setFormData(prev => ({
      ...prev,
      image_url: url
    }));
  };

  return (
    <div className="space-y-6" data-testid="blogs-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Manage Blog Posts</h2>
        <button onClick={() => setShowForm(!showForm)} className="retro-button" data-testid="add-blog-button">
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span className="ml-2">{showForm ? 'Cancel' : 'New Post'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="cassette-card p-8 space-y-6" data-testid="blog-form">
          <div className="bg-[#2C1810]/50 p-4 rounded border-2 border-[#E8934D]">
            <p className="text-[#F4E4C1] text-sm mb-2">📝 <strong>Tips for Remarkable Notes:</strong></p>
            <ul className="text-[#F4E4C1] text-xs space-y-1 ml-4">
              <li>• Export your notes as PNG or PDF from Remarkable</li>
              <li>• Upload multiple images at once using the button below</li>
              <li>• Images will appear in your content automatically</li>
              <li>• You can rearrange them in the content field</li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Blog Post Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            className="w-full text-lg"
            data-testid="blog-title-input"
          />
          
          <input
            type="text"
            placeholder="Short excerpt (1-2 sentences for preview)"
            value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            required
            className="w-full"
            data-testid="blog-excerpt-input"
          />

          <div>
            <label className="block text-[#2C1810] font-bold mb-2">Blog Content & Images</label>
            <textarea
              placeholder="Write your blog content here. Use line breaks for paragraphs. Images will be inserted where you uploaded them."
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              rows={15}
              className="w-full font-mono text-sm"
              data-testid="blog-content-input"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-[#2C1810] font-bold">Upload Remarkable Notes (Images)</label>
            <div className="flex gap-3">
              <label className="retro-button cursor-pointer inline-flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Images'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="blog-image-upload"
                />
              </label>
              <button
                type="button"
                onClick={insertImagePlaceholder}
                className="retro-button bg-[#4D9E7C]"
              >
                Add Image Placeholder
              </button>
            </div>
            <p className="text-[#2C1810] text-xs">Tip: Select multiple files to upload all your Remarkable notes at once!</p>
          </div>

          <div className="space-y-3">
            <label className="block text-[#2C1810] font-bold">Featured Image (for blog card thumbnail)</label>
            <div className="flex gap-3 flex-wrap">
              <input
                type="url"
                placeholder="Enter image URL"
                value={formData.image_url || ''}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="flex-1 min-w-[200px]"
                data-testid="blog-featured-image-input"
              />
              <label className="retro-button cursor-pointer inline-flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFeaturedImageUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="blog-featured-image-upload"
                />
              </label>
              <button
                type="button"
                onClick={() => setShowUnsplashPicker(true)}
                className="retro-button bg-blue-600 hover:bg-blue-700 inline-flex items-center"
                data-testid="blog-choose-image"
              >
                <ImageIcon className="w-5 h-5 mr-2" />
                Choose Image
              </button>
            </div>
            {formData.image_url && (
              <div className="mt-2">
                <img
                  src={formData.image_url}
                  alt="Featured image preview"
                  className="h-20 w-32 object-cover rounded border-2 border-[#6B4423]"
                />
              </div>
            )}
            <p className="text-[#2C1810] text-xs">Choose: paste a URL, upload an image, browse your library, or search stock photos</p>
          </div>

          <input
            type="url"
            placeholder="Video URL (optional - YouTube, Vimeo, etc.)"
            value={formData.video_url || ''}
            onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
            className="w-full"
            data-testid="blog-video-url-input"
          />

          <button type="submit" className="retro-button w-full" data-testid="save-blog-button">
            <Save className="w-5 h-5 inline mr-2" />
            {editingBlog ? 'Update' : 'Publish'} Blog Post
          </button>
        </form>
      )}

      <div className="space-y-4">
        {blogs.map((blog) => (
          <div key={blog.id} className="cassette-card p-6" data-testid={`blog-item-${blog.id}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-2xl retro-title text-[#3D2817]">{blog.title}</h3>
                <p className="text-[#3D2817] mt-2">{blog.excerpt}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleEdit(blog)} className="text-[#6B4423] hover:text-[#3D2817]" data-testid={`edit-blog-${blog.id}`}>
                  <FileText className="w-5 h-5" />
                </button>
                <button onClick={() => handleDelete(blog.id)} className="text-[#6B4423] hover:text-[#3D2817]" data-testid={`delete-blog-${blog.id}`}>
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Universal Image Picker Modal */}
      <MediaPickerModal
        isOpen={showUnsplashPicker}
        onClose={() => setShowUnsplashPicker(false)}
        onSelect={handleUnsplashSelect}
      />
    </div>
  );
}

// Videos Manager
function VideosManager() {
  const [videos, setVideos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    category: 'nutrition',
    thumbnail_url: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Normalize YouTube URL to support multiple formats
    let normalizedUrl = formData.youtube_url;
    if (normalizedUrl) {
      // Extract video ID from various YouTube URL formats
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /youtube\.com\/shorts\/([^&\n?#]+)/,
        /youtube\.com\/live\/([^&\n?#]+)/
      ];
      
      for (const pattern of patterns) {
        const match = normalizedUrl.match(pattern);
        if (match && match[1]) {
          normalizedUrl = `https://www.youtube.com/watch?v=${match[1]}`;
          break;
        }
      }
    }
    
    const dataToSubmit = { ...formData, youtube_url: normalizedUrl };
    
    try {
      if (editingVideo) {
        // Update existing video
        await axios.put(`${API}/admin/videos/${editingVideo.id}`, dataToSubmit, { withCredentials: true });
        toast.success('Video updated!');
      } else {
        // Create new video
        await axios.post(`${API}/admin/videos`, dataToSubmit, { withCredentials: true });
        toast.success('Video added!');
      }
      resetForm();
      loadVideos();
    } catch (error) {
      toast.error(editingVideo ? 'Failed to update video' : 'Failed to save video');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-video`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, file_path: response.data.file_path });
      toast.success('Video uploaded!');
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/admin/videos/${id}`, { withCredentials: true });
      toast.success('Video deleted!');
      loadVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      youtube_url: video.youtube_url || '',
      category: video.category,
      thumbnail_url: video.thumbnail_url || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', youtube_url: '', category: 'nutrition', thumbnail_url: '' });
    setShowForm(false);
    setEditingVideo(null);
  };

  return (
    <div className="space-y-6" data-testid="videos-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Manage Videos</h2>
        <button onClick={() => setShowForm(!showForm)} className="retro-button" data-testid="add-video-button">
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span className="ml-2">{showForm ? 'Cancel' : 'New Video'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="cassette-card p-6 space-y-4" data-testid="video-form">
          <h3 className="text-xl retro-title text-[#2C1810] mb-4">
            {editingVideo ? 'Edit Video' : 'Add New Video'}
          </h3>
          <input
            type="text"
            placeholder="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded"
            data-testid="video-title-input"
          />
          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={3}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded"
            data-testid="video-description-input"
          />
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded"
            data-testid="video-category-select"
          >
            <option value="nutrition">Nutrition</option>
            <option value="qigong">Qi Gong</option>
            <option value="daily_life">Daily Life Advice</option>
          </select>
          <div>
            <label className="block text-[#3D2817] font-bold mb-2">YouTube URL</label>
            <input
              type="url"
              placeholder="YouTube URL (any format: watch, shorts, embed, youtu.be)"
              value={formData.youtube_url}
              onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded"
              data-testid="video-youtube-input"
            />
            <p className="text-xs text-[#6B4423] mt-1">
              Supports: youtube.com/watch?v=..., youtu.be/..., youtube.com/shorts/..., youtube.com/live/..., youtube.com/embed/...
            </p>
          </div>
          <div>
            <label className="block text-[#3D2817] mb-2">Or upload video file:</label>
            <input
              type="file"
              accept="video/*"
              onChange={handleFileUpload}
              disabled={uploading}
              className="w-full"
              data-testid="video-file-input"
            />
            {uploading && <p className="text-[#3D2817] mt-2">Uploading...</p>}
          </div>
          <button type="submit" className="retro-button" data-testid="save-video-button">
            <Save className="w-5 h-5 inline mr-2" />
            {editingVideo ? 'Update Video' : 'Add Video'}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {videos.map((video) => (
          <div key={video.id} className="cassette-card p-4" data-testid={`video-item-${video.id}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-xl retro-title text-[#3D2817]">{video.title}</h3>
                <p className="text-[#3D2817] text-sm mt-1">{video.description}</p>
                <span className="text-xs text-[#6B4423] mt-2 inline-block">{video.category}</span>
                {video.youtube_url && (
                  <p className="text-xs text-[#6B4423] mt-1 truncate">YouTube: {video.youtube_url}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleEdit(video)} 
                  className="text-[#4D9E7C] hover:text-[#E8934D]" 
                  data-testid={`edit-video-${video.id}`}
                  title="Edit video"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleDelete(video.id)} 
                  className="text-[#E85D4D] hover:text-[#3D2817]" 
                  data-testid={`delete-video-${video.id}`}
                  title="Delete video"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Podcasts Manager
function PodcastsManager() {
  const [podcasts, setPodcasts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', file_path: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadPodcasts();
  }, []);

  const loadPodcasts = async () => {
    try {
      const response = await axios.get(`${API}/podcasts`);
      setPodcasts(response.data);
    } catch (error) {
      toast.error('Failed to load podcasts');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-podcast`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, file_path: response.data.file_path });
      toast.success('Podcast uploaded!');
    } catch (error) {
      toast.error('Failed to upload podcast');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_path) {
      toast.error('Please upload an audio file');
      return;
    }
    try {
      await axios.post(`${API}/admin/podcasts`, formData, { withCredentials: true });
      toast.success('Podcast added!');
      resetForm();
      loadPodcasts();
    } catch (error) {
      toast.error('Failed to save podcast');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this podcast?')) return;
    try {
      await axios.delete(`${API}/admin/podcasts/${id}`, { withCredentials: true });
      toast.success('Podcast deleted!');
      loadPodcasts();
    } catch (error) {
      toast.error('Failed to delete podcast');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', file_path: '' });
    setShowForm(false);
  };

  return (
    <div className="space-y-6" data-testid="podcasts-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Manage Podcasts</h2>
        <button onClick={() => setShowForm(!showForm)} className="retro-button" data-testid="add-podcast-button">
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span className="ml-2">{showForm ? 'Cancel' : 'New Episode'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="cassette-card p-6 space-y-4" data-testid="podcast-form">
          <input
            type="text"
            placeholder="Episode Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            className="w-full"
            data-testid="podcast-title-input"
          />
          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={3}
            className="w-full"
            data-testid="podcast-description-input"
          />
          <div>
            <label className="block text-[#3D2817] mb-2">Upload audio file:</label>
            <input
              type="file"
              accept="audio/*"
              onChange={handleFileUpload}
              disabled={uploading}
              className="w-full"
              data-testid="podcast-file-input"
            />
            {uploading && <p className="text-[#3D2817] mt-2">Uploading...</p>}
            {formData.file_path && <p className="text-[#3D2817] mt-2">✓ File uploaded</p>}
          </div>
          <button type="submit" className="retro-button" data-testid="save-podcast-button">
            <Save className="w-5 h-5 inline mr-2" />
            Add Podcast
          </button>
        </form>
      )}

      <div className="space-y-4">
        {podcasts.map((podcast) => (
          <div key={podcast.id} className="cassette-card p-6" data-testid={`podcast-item-${podcast.id}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-2xl retro-title text-[#3D2817]">{podcast.title}</h3>
                <p className="text-[#3D2817] mt-2">{podcast.description}</p>
              </div>
              <button onClick={() => handleDelete(podcast.id)} className="text-[#6B4423] hover:text-[#3D2817]" data-testid={`delete-podcast-${podcast.id}`}>
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Continue with other managers...
function BookManager() {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image_url: '',
    purchase_link: ''
  });

  useEffect(() => {
    loadBook();
  }, []);

  const loadBook = async () => {
    try {
      const response = await axios.get(`${API}/book`);
      if (response.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Failed to load book');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/book`, formData, { withCredentials: true });
      toast.success('Book information updated!');
    } catch (error) {
      toast.error('Failed to update book');
    }
  };

  return (
    <div className="space-y-6" data-testid="book-manager">
      <h2 className="text-3xl retro-title">Manage Book</h2>
      <form onSubmit={handleSubmit} className="cassette-card p-6 space-y-4" data-testid="book-form">
        <input
          type="text"
          placeholder="Book Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="w-full"
          data-testid="book-title-input"
        />
        <textarea
          placeholder="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          rows={6}
          className="w-full"
          data-testid="book-description-input"
        />
        <input
          type="url"
          placeholder="Cover Image URL"
          value={formData.cover_image_url}
          onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
          className="w-full"
          data-testid="book-cover-input"
        />
        <input
          type="url"
          placeholder="Purchase Link"
          value={formData.purchase_link}
          onChange={(e) => setFormData({ ...formData, purchase_link: e.target.value })}
          className="w-full"
          data-testid="book-purchase-input"
        />
        <button type="submit" className="retro-button" data-testid="save-book-button">
          <Save className="w-5 h-5 inline mr-2" />
          Update Book
        </button>
      </form>
    </div>
  );
}

function ScheduleManager() {
  const [formData, setFormData] = useState({
    clinic_name: '',
    address: '',
    hours: ''
  });

  useEffect(() => {
    loadSchedule();
  }, []);

  const loadSchedule = async () => {
    try {
      const response = await axios.get(`${API}/schedule`);
      if (response.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Failed to load schedule');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/schedule`, formData, { withCredentials: true });
      toast.success('Schedule updated!');
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-manager">
      <h2 className="text-3xl retro-title">Manage Schedule</h2>
      <form onSubmit={handleSubmit} className="cassette-card p-6 space-y-4" data-testid="schedule-form">
        <input
          type="text"
          placeholder="Clinic Name"
          value={formData.clinic_name}
          onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })}
          required
          className="w-full"
          data-testid="clinic-name-input"
        />
        <textarea
          placeholder="Address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          required
          rows={3}
          className="w-full"
          data-testid="clinic-address-input"
        />
        <textarea
          placeholder="Hours (e.g., Mon-Fri: 9am-5pm)"
          value={formData.hours}
          onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
          required
          rows={4}
          className="w-full"
          data-testid="clinic-hours-input"
        />
        <button type="submit" className="retro-button" data-testid="save-schedule-button">
          <Save className="w-5 h-5 inline mr-2" />
          Update Schedule
        </button>
      </form>
    </div>
  );
}

function AboutManager() {
  const [formData, setFormData] = useState({
    philosophy: '',
    approach: '',
    mental_health: '',
    sports_medicine: '',
    reproductive_health: '',
    pain_management: '',
    general_care: '',
    nutrition: '',
    image_1_url: '',
    image_2_url: ''
  });
  const [showUnsplash1, setShowUnsplash1] = useState(false);
  const [showUnsplash2, setShowUnsplash2] = useState(false);

  useEffect(() => {
    loadAbout();
  }, []);

  const loadAbout = async () => {
    try {
      const response = await axios.get(`${API}/about`);
      if (response.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Failed to load about');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/about`, formData, { withCredentials: true });
      toast.success('About section updated!');
    } catch (error) {
      toast.error('Failed to update about');
    }
  };

  return (
    <div className="space-y-6" data-testid="about-manager">
      <h2 className="text-3xl retro-title">Manage About & Specializations</h2>
      <p className="text-[#F4E4C1] mb-4">Edit all text content that appears on the About page and homepage specializations section.</p>
      
      <form onSubmit={handleSubmit} className="cassette-card p-8 space-y-6" data-testid="about-form">
        <div>
          <label className="block text-[#2C1810] mb-2 font-bold text-lg">Philosophy</label>
          <textarea
            placeholder="Your philosophy on acupuncture and healing"
            value={formData.philosophy}
            onChange={(e) => setFormData({ ...formData, philosophy: e.target.value })}
            required
            rows={6}
            className="w-full"
            data-testid="philosophy-input"
          />
        </div>
        
        <div>
          <label className="block text-[#2C1810] mb-2 font-bold text-lg">Approach</label>
          <textarea
            placeholder="Your approach to treatment and patient care"
            value={formData.approach}
            onChange={(e) => setFormData({ ...formData, approach: e.target.value })}
            required
            rows={6}
            className="w-full"
            data-testid="approach-input"
          />
        </div>

        <div className="border-t-4 border-[#E85D4D] pt-6 mt-6">
          <h3 className="text-2xl retro-title text-[#2C1810] mb-4">Specializations</h3>
          <p className="text-[#2C1810] mb-6">These appear on homepage and About page with detailed descriptions.</p>
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">🧠 Mental Health Acupuncture</label>
          <textarea
            placeholder="Describe your mental health acupuncture services (anxiety, depression, stress, etc.)"
            value={formData.mental_health}
            onChange={(e) => setFormData({ ...formData, mental_health: e.target.value })}
            rows={5}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">⚡ Sports Medicine</label>
          <textarea
            placeholder="Describe your sports medicine acupuncture services"
            value={formData.sports_medicine}
            onChange={(e) => setFormData({ ...formData, sports_medicine: e.target.value })}
            rows={5}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">🌸 Reproductive Health (Women's & Men's)</label>
          <textarea
            placeholder="Describe reproductive health services for both women and men"
            value={formData.reproductive_health}
            onChange={(e) => setFormData({ ...formData, reproductive_health: e.target.value })}
            rows={5}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">💪 Pain Management</label>
          <textarea
            placeholder="Describe pain management and chronic pain services"
            value={formData.pain_management}
            onChange={(e) => setFormData({ ...formData, pain_management: e.target.value })}
            rows={5}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">🏥 General Care</label>
          <textarea
            placeholder="Describe general wellness and preventive care services"
            value={formData.general_care}
            onChange={(e) => setFormData({ ...formData, general_care: e.target.value })}
            rows={5}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">🥗 Nutrition Guidance</label>
          <textarea
            placeholder="Describe nutrition counseling and dietary guidance services"
            value={formData.nutrition}
            onChange={(e) => setFormData({ ...formData, nutrition: e.target.value })}
            rows={5}
            className="w-full"
          />
        </div>

        <div className="border-t-4 border-[#E85D4D] pt-6 mt-6">
          <h3 className="text-2xl retro-title text-[#2C1810] mb-4">About Page Images</h3>
          <p className="text-[#2C1810] mb-6">Select two background/decorative images for the About page.</p>
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">📸 Image 1</label>
          <button
            type="button"
            onClick={() => setShowUnsplash1(true)}
            className="retro-button mb-2"
          >
            <ImageIcon className="w-5 h-5 inline mr-2" />
            Browse Images from Unsplash
          </button>
          {formData.image_1_url && (
            <div className="mt-2 relative">
              <img 
                src={formData.image_1_url} 
                alt="Selected image 1" 
                className="w-full h-48 object-cover rounded"
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, image_1_url: '' })}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-[#2C1810] mb-2 font-bold">📸 Image 2</label>
          <button
            type="button"
            onClick={() => setShowUnsplash2(true)}
            className="retro-button mb-2"
          >
            <ImageIcon className="w-5 h-5 inline mr-2" />
            Browse Images from Unsplash
          </button>
          {formData.image_2_url && (
            <div className="mt-2 relative">
              <img 
                src={formData.image_2_url} 
                alt="Selected image 2" 
                className="w-full h-48 object-cover rounded"
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, image_2_url: '' })}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <button type="submit" className="retro-button w-full" data-testid="save-about-button">
          <Save className="w-5 h-5 inline mr-2" />
          Update All About Content
        </button>
      </form>

      {showUnsplash1 && (
        <UnsplashImagePicker
          isOpen={showUnsplash1}
          onSelect={(imageData) => {
            setFormData({ ...formData, image_1_url: imageData.url });
            setShowUnsplash1(false);
          }}
          onClose={() => setShowUnsplash1(false)}
        />
      )}

      {showUnsplash2 && (
        <UnsplashImagePicker
          isOpen={showUnsplash2}
          onSelect={(imageData) => {
            setFormData({ ...formData, image_2_url: imageData.url });
            setShowUnsplash2(false);
          }}
          onClose={() => setShowUnsplash2(false)}
        />
      )}
    </div>
  );
}

function GroupStudyManager() {
  const [formData, setFormData] = useState({
    zoom_link: '',
    description: '',
    day: 'Sunday',
    time: '7:00 AM CST'
  });

  useEffect(() => {
    loadGroupStudy();
  }, []);

  const loadGroupStudy = async () => {
    try {
      const response = await axios.get(`${API}/group-study`);
      if (response.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Failed to load group study');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/group-study`, formData, { withCredentials: true });
      toast.success('Group study session updated!');
    } catch (error) {
      toast.error('Failed to update group study');
    }
  };

  return (
    <div className="space-y-6" data-testid="group-study-manager">
      <h2 className="text-3xl retro-title">Manage Group Study Session</h2>
      <form onSubmit={handleSubmit} className="cassette-card p-6 space-y-4" data-testid="group-study-form">
        <input
          type="url"
          placeholder="Zoom Link"
          value={formData.zoom_link}
          onChange={(e) => setFormData({ ...formData, zoom_link: e.target.value })}
          required
          className="w-full"
          data-testid="zoom-link-input"
        />
        <textarea
          placeholder="Session Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          rows={4}
          className="w-full"
          data-testid="session-description-input"
        />
        <input
          type="text"
          placeholder="Day (e.g., Sunday)"
          value={formData.day}
          onChange={(e) => setFormData({ ...formData, day: e.target.value })}
          required
          className="w-full"
          data-testid="session-day-input"
        />
        <input
          type="text"
          placeholder="Time (e.g., 7:00 AM CST)"
          value={formData.time}
          onChange={(e) => setFormData({ ...formData, time: e.target.value })}
          required
          className="w-full"
          data-testid="session-time-input"
        />
        <button type="submit" className="retro-button" data-testid="save-group-study-button">
          <Save className="w-5 h-5 inline mr-2" />
          Update Group Study
        </button>
      </form>
    </div>
  );
}

// Courses Manager
function CoursesManager() {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showChapterForm, setShowChapterForm] = useState(false);
  const [showContentForm, setShowContentForm] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    author: '',
    date: '',
    price: 0,
    is_free: true,
    cover_image_url: '',
    preview_description: ''
  });
  const [chapterForm, setChapterForm] = useState({
    title: '',
    description: '',
    order: 1
  });
  const [contentForm, setContentForm] = useState({
    type: 'text',
    title: '',
    content_url: '',
    content_text: '',
    order: 1,
    is_preview: false
  });
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizForm, setQuizForm] = useState({
    title: '',
    passing_score: 70,
    order: 1
  });
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    options: ['', '', '', ''],
    correct_answer_index: 0,
    order: 1
  });

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    }
  };

  const loadCourseDetails = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setSelectedCourse(response.data);
    } catch (error) {
      toast.error('Failed to load course details');
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/courses`, courseForm, {
        withCredentials: true
      });
      toast.success('Course created!');
      setShowCourseForm(false);
      setCourseForm({
        title: '', description: '', author: '', date: '', price: 0,
        is_free: true, cover_image_url: '', preview_description: ''
      });
      loadCourses();
    } catch (error) {
      toast.error('Failed to create course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course? This will also delete all chapters, content, and enrollments.')) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`, {
        withCredentials: true
      });
      toast.success('Course deleted');
      setSelectedCourse(null);
      loadCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleCreateChapter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/courses/${selectedCourse.id}/chapters`, chapterForm, {
        withCredentials: true
      });
      toast.success('Chapter added!');
      setShowChapterForm(false);
      setChapterForm({ title: '', description: '', order: 1 });
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to add chapter');
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/chapters/${selectedChapter.id}/content`, contentForm, {
        withCredentials: true
      });
      toast.success('Content added!');
      setShowContentForm(false);
      setContentForm({ type: 'text', title: '', content_url: '', content_text: '', order: 1, is_preview: false });
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to add content');
    }
  };

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/chapters/${selectedChapter.id}/quizzes`, quizForm, {
        withCredentials: true
      });
      toast.success('Quiz created!');
      setSelectedQuiz(response.data);
      setShowQuizForm(false);
      setShowQuestionForm(true);
      setQuizForm({ title: '', passing_score: 70, order: 1 });
    } catch (error) {
      toast.error('Failed to create quiz');
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/quizzes/${selectedQuiz.id}/questions`, questionForm, {
        withCredentials: true
      });
      toast.success('Question added!');
      setQuestionForm({
        question_text: '',
        options: ['', '', '', ''],
        correct_answer_index: 0,
        order: (questionForm.order || 0) + 1
      });
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  return (
    <div className="space-y-6" data-testid="courses-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Courses</h2>
        <button
          onClick={() => setShowCourseForm(!showCourseForm)}
          className="retro-button"
        >
          {showCourseForm ? 'Cancel' : '+ New Course'}
        </button>
      </div>

      {/* Create Course Form */}
      {showCourseForm && (
        <form onSubmit={handleCreateCourse} className="cassette-card p-6 space-y-4">
          <h3 className="text-xl retro-title text-[#2C1810]">Create New Course</h3>
          
          <input
            type="text"
            placeholder="Course Title"
            value={courseForm.title}
            onChange={(e) => setCourseForm({...courseForm, title: e.target.value})}
            required
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
          />
          
          <textarea
            placeholder="Full Description"
            value={courseForm.description}
            onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
            required
            rows={4}
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
          />
          
          <textarea
            placeholder="Preview Description (for non-enrolled users)"
            value={courseForm.preview_description}
            onChange={(e) => setCourseForm({...courseForm, preview_description: e.target.value})}
            rows={2}
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
          />
          
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Author"
              value={courseForm.author}
              onChange={(e) => setCourseForm({...courseForm, author: e.target.value})}
              required
              className="px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
            />
            
            <input
              type="text"
              placeholder="Date/Year"
              value={courseForm.date}
              onChange={(e) => setCourseForm({...courseForm, date: e.target.value})}
              required
              className="px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
            />
          </div>
          
          <input
            type="text"
            placeholder="Cover Image URL"
            value={courseForm.cover_image_url}
            onChange={(e) => setCourseForm({...courseForm, cover_image_url: e.target.value})}
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
          />
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-[#2C1810]">
              <input
                type="checkbox"
                checked={courseForm.is_free}
                onChange={(e) => setCourseForm({...courseForm, is_free: e.target.checked, price: e.target.checked ? 0 : courseForm.price})}
              />
              <span>Free Course</span>
            </label>
            
            {!courseForm.is_free && (
              <input
                type="number"
                placeholder="Price"
                value={courseForm.price}
                onChange={(e) => setCourseForm({...courseForm, price: parseFloat(e.target.value)})}
                min="0"
                step="0.01"
                required
                className="px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
              />
            )}
          </div>
          
          <button type="submit" className="retro-button w-full">Create Course</button>
        </form>
      )}

      {/* Course List */}
      {!selectedCourse ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((course) => (
            <div key={course.id} className="cassette-card p-4">
              <h3 className="text-xl retro-title text-[#2C1810] mb-2">{course.title}</h3>
              <p className="text-sm text-[#6B4423] mb-2">By {course.author}</p>
              <p className="text-sm text-[#6B4423] mb-4">
                {course.is_free ? 'FREE' : `$${course.price}`}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => loadCourseDetails(course.id)}
                  className="retro-button-small flex-1"
                >
                  Manage
                </button>
                <button
                  onClick={() => handleDeleteCourse(course.id)}
                  className="retro-button-small bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Course Details View */
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setSelectedCourse(null)} className="retro-button-small">
              ← Back to Courses
            </button>
            <h3 className="text-2xl retro-title">{selectedCourse.title}</h3>
          </div>

          {/* Chapters */}
          <div className="cassette-card p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl retro-title text-[#2C1810]">Chapters ({selectedCourse.chapters?.length || 0})</h4>
              <button
                onClick={() => setShowChapterForm(!showChapterForm)}
                className="retro-button-small"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {showChapterForm && (
              <form onSubmit={handleCreateChapter} className="mb-4 p-4 bg-[#F4E4C1] rounded-lg space-y-3">
                <h5 className="font-bold text-[#2C1810]">Add New Chapter</h5>
                <input
                  type="text"
                  placeholder="Chapter Title *"
                  value={chapterForm.title}
                  onChange={(e) => setChapterForm({...chapterForm, title: e.target.value})}
                  required
                  className="w-full px-3 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
                <textarea
                  placeholder="Chapter Description (optional)"
                  value={chapterForm.description}
                  onChange={(e) => setChapterForm({...chapterForm, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
                <input
                  type="number"
                  placeholder="Order Number *"
                  value={chapterForm.order}
                  onChange={(e) => setChapterForm({...chapterForm, order: parseInt(e.target.value)})}
                  required
                  min="1"
                  className="w-full px-3 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
                <button type="submit" className="retro-button-small w-full">Add Chapter</button>
              </form>
            )}

            <div className="space-y-4">
              {selectedCourse.chapters?.length === 0 && (
                <p className="text-[#6B4423] text-center py-4">No chapters yet. Click + to add one.</p>
              )}
              {selectedCourse.chapters?.map((chapter) => (
                <div key={chapter.id} className="p-4 bg-white rounded border-2 border-[#6B4423]">
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="flex-1">
                      <p className="font-bold text-lg text-[#2C1810]">{chapter.order}. {chapter.title}</p>
                      {chapter.description && <p className="text-sm text-[#6B4423]">{chapter.description}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedChapter(chapter);
                          setShowContentForm(true);
                          setShowQuizForm(false);
                        }}
                        className="retro-button-small"
                      >
                        + Content
                      </button>
                      <button
                        onClick={() => {
                          setSelectedChapter(chapter);
                          setShowQuizForm(true);
                          setShowContentForm(false);
                        }}
                        className="retro-button-small bg-[#E8934D]"
                      >
                        + Quiz
                      </button>
                    </div>
                  </div>

                  {/* Content Items in this Chapter */}
                  {chapter.content && chapter.content.length > 0 && (
                    <div className="mt-3 pl-4 border-l-4 border-[#E8B67A]">
                      <p className="text-xs font-bold text-[#6B4423] mb-2">CONTENT ({chapter.content.length}):</p>
                      <div className="space-y-1">
                        {chapter.content.map((content) => (
                          <div key={content.id} className="flex items-center gap-2 text-sm">
                            <span className="text-[#6B4423]">{content.order}.</span>
                            <span className="px-2 py-0.5 bg-[#E8934D] text-white text-xs rounded">{content.type}</span>
                            <span className="flex-1 text-[#2C1810]">{content.title}</span>
                            {content.is_preview && <span className="text-xs text-green-600">🔓 Preview</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quizzes in this Chapter */}
                  {chapter.quizzes && chapter.quizzes.length > 0 && (
                    <div className="mt-3 pl-4 border-l-4 border-[#E85D4D]">
                      <p className="text-xs font-bold text-[#6B4423] mb-2">QUIZZES ({chapter.quizzes.length}):</p>
                      <div className="space-y-1">
                        {chapter.quizzes.map((quiz) => (
                          <div key={quiz.id} className="flex items-center gap-2 text-sm">
                            <span className="text-[#6B4423]">{quiz.order}.</span>
                            <span className="px-2 py-0.5 bg-[#E85D4D] text-white text-xs rounded">QUIZ</span>
                            <span className="flex-1 text-[#2C1810]">{quiz.title}</span>
                            <span className="text-xs text-[#6B4423]">Pass: {quiz.passing_score}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {(!chapter.content || chapter.content.length === 0) && (!chapter.quizzes || chapter.quizzes.length === 0) && (
                    <p className="text-xs text-[#6B4423] italic mt-2 pl-4">No content or quizzes added yet</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add Content Form */}
          {showContentForm && selectedChapter && (
            <form onSubmit={handleCreateContent} className="cassette-card p-6 space-y-4">
              <h4 className="text-xl retro-title text-[#2C1810]">Add Content to: {selectedChapter.title}</h4>
              
              <select
                value={contentForm.type}
                onChange={(e) => setContentForm({...contentForm, type: e.target.value})}
                className="w-full px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810]"
              >
                <option value="text">Text</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
                <option value="slides">Slides/Image</option>
              </select>
              
              <input
                type="text"
                placeholder="Content Title"
                value={contentForm.title}
                onChange={(e) => setContentForm({...contentForm, title: e.target.value})}
                required
                className="w-full px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
              />
              
              {contentForm.type === 'text' ? (
                <textarea
                  placeholder="Content Text"
                  value={contentForm.content_text}
                  onChange={(e) => setContentForm({...contentForm, content_text: e.target.value})}
                  rows={6}
                  className="w-full px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
              ) : (
                <input
                  type="text"
                  placeholder="Content URL (video/audio/image)"
                  value={contentForm.content_url}
                  onChange={(e) => setContentForm({...contentForm, content_url: e.target.value})}
                  className="w-full px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
              )}
              
              <div className="flex gap-4">
                <input
                  type="number"
                  placeholder="Order"
                  value={contentForm.order}
                  onChange={(e) => setContentForm({...contentForm, order: parseInt(e.target.value)})}
                  className="px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
                <label className="flex items-center gap-2 text-[#2C1810]">
                  <input
                    type="checkbox"
                    checked={contentForm.is_preview}
                    onChange={(e) => setContentForm({...contentForm, is_preview: e.target.checked})}
                  />
                  <span>Free Preview</span>
                </label>
              </div>
              
              <div className="flex gap-2">
                <button type="submit" className="retro-button flex-1">Add Content</button>
                <button
                  type="button"
                  onClick={() => {
                    setShowContentForm(false);
                    setSelectedChapter(null);
                  }}
                  className="retro-button-small"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Add Quiz Form */}
          {showQuizForm && selectedChapter && !selectedQuiz && (
            <form onSubmit={handleCreateQuiz} className="cassette-card p-6 space-y-4">
              <h4 className="text-xl retro-title text-[#2C1810]">Create Quiz for: {selectedChapter.title}</h4>
              
              <input
                type="text"
                placeholder="Quiz Title (e.g., Chapter 1 Quiz)"
                value={quizForm.title}
                onChange={(e) => setQuizForm({...quizForm, title: e.target.value})}
                required
                className="w-full px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="number"
                  placeholder="Passing Score (%)"
                  value={quizForm.passing_score}
                  onChange={(e) => setQuizForm({...quizForm, passing_score: parseInt(e.target.value)})}
                  min="0"
                  max="100"
                  required
                  className="px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
                <input
                  type="number"
                  placeholder="Order"
                  value={quizForm.order}
                  onChange={(e) => setQuizForm({...quizForm, order: parseInt(e.target.value)})}
                  className="px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
              </div>
              
              <div className="flex gap-2">
                <button type="submit" className="retro-button flex-1">Create Quiz</button>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuizForm(false);
                    setSelectedChapter(null);
                  }}
                  className="retro-button-small"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Add Questions to Quiz Form */}
          {showQuestionForm && selectedQuiz && (
            <div className="cassette-card p-6 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-xl retro-title text-[#2C1810]">Add Questions to: {selectedQuiz.title}</h4>
                <button
                  onClick={() => {
                    setShowQuestionForm(false);
                    setSelectedQuiz(null);
                    setSelectedChapter(null);
                    loadCourseDetails(selectedCourse.id);
                  }}
                  className="retro-button-small"
                >
                  Done
                </button>
              </div>

              <form onSubmit={handleAddQuestion} className="space-y-4 p-4 bg-[#F4E4C1] rounded">
                <textarea
                  placeholder="Question Text"
                  value={questionForm.question_text}
                  onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                  required
                  rows={3}
                  className="w-full px-4 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                />
                
                <div className="space-y-2">
                  <p className="text-sm font-bold text-[#3D2817]">Answer Options:</p>
                  {questionForm.options.map((option, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="radio"
                        name="correct_answer"
                        checked={questionForm.correct_answer_index === index}
                        onChange={() => setQuestionForm({...questionForm, correct_answer_index: index})}
                        title="Mark as correct answer"
                      />
                      <input
                        type="text"
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...questionForm.options];
                          newOptions[index] = e.target.value;
                          setQuestionForm({...questionForm, options: newOptions});
                        }}
                        required
                        className="flex-1 px-3 py-2 rounded border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
                      />
                    </div>
                  ))}
                  <p className="text-xs text-[#6B4423]">Select the radio button for the correct answer</p>
                </div>
                
                <button type="submit" className="retro-button w-full">Add Question</button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Tests Manager
function TestsManager() {
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showTestForm, setShowTestForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showQuestionPoolModal, setShowQuestionPoolModal] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionPool, setQuestionPool] = useState([]);
  const [selectedPoolQuestions, setSelectedPoolQuestions] = useState([]);
  const [poolFilters, setPoolFilters] = useState({ category: '', level: '', search: '' });
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [testForm, setTestForm] = useState({
    title: '',
    description: '',
    tier: 'basic',
    category: 'acupuncture',
    question_count: 10,
    passing_score: 70
  });
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    options: ['', '', '', ''],
    correct_answer_index: 0,
    order: 1
  });

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`);
      setTests(response.data);
    } catch (error) {
      toast.error('Failed to load tests');
    }
  };

  const loadQuestions = async (testId) => {
    try {
      const response = await axios.get(`${API}/admin/tests/${testId}/questions`, {
        withCredentials: true
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to load questions:', error);
      setQuestions([]);
    }
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    
    // Calculate time limit: 1.25 seconds per question for comps/advanced
    const time_limit_seconds = ['comps_ready', 'advanced'].includes(testForm.tier)
      ? Math.round(testForm.question_count * 1.25)
      : null;

    const testData = {
      ...testForm,
      time_limit_seconds
    };

    try {
      const response = await axios.post(`${API}/admin/tests`, testData, {
        withCredentials: true
      });
      toast.success('Test created! Now add questions.');
      setSelectedTest(response.data);
      setShowTestForm(false);
      setShowQuestionForm(true);
      setTestForm({ title: '', description: '', tier: 'basic', category: 'acupuncture', question_count: 10, passing_score: 70 });
      loadTests();
    } catch (error) {
      toast.error('Failed to create test');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Delete this test? This will also delete all questions and attempts.')) return;
    
    try {
      await axios.delete(`${API}/admin/tests/${testId}`, {
        withCredentials: true
      });
      toast.success('Test deleted');
      setSelectedTest(null);
      loadTests();
    } catch (error) {
      toast.error('Failed to delete test');
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/tests/${selectedTest.id}/questions`, questionForm, {
        withCredentials: true
      });
      toast.success('Question added!');
      await loadQuestions(selectedTest.id);
      setQuestionForm({
        question_text: '',
        options: ['', '', '', ''],
        correct_answer_index: 0,
        order: questions.length + 2
      });
      setShowQuestionForm(false);
      setSelectedQuestionIndex(null);
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    
    try {
      await axios.delete(`${API}/admin/test-questions/${questionId}`, {
        withCredentials: true
      });
      toast.success('Question deleted');
      await loadQuestions(selectedTest.id);
      setSelectedQuestionIndex(null);
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleEditQuestion = (question, index) => {
    setQuestionForm(question);
    setSelectedQuestionIndex(index);
    setShowQuestionForm(true);
  };

  const loadQuestionPool = async () => {
    try {
      const params = new URLSearchParams();
      if (poolFilters.category) params.append('category', poolFilters.category);
      if (poolFilters.level) params.append('level', poolFilters.level);
      if (poolFilters.search) params.append('search', poolFilters.search);
      
      const response = await axios.get(`${API}/admin/question-pool?${params.toString()}`, {
        withCredentials: true
      });
      setQuestionPool(response.data);
    } catch (error) {
      console.error('Failed to load question pool:', error);
    }
  };

  const handleAddPoolQuestionsToTest = async () => {
    if (selectedPoolQuestions.length === 0) {
      toast.error('Please select questions first');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/add-questions-to-test/${selectedTest.id}`, selectedPoolQuestions, {
        withCredentials: true
      });
      toast.success(`Added ${selectedPoolQuestions.length} questions to test!`);
      setShowQuestionPoolModal(false);
      setSelectedPoolQuestions([]);
      loadQuestions(selectedTest.id);
    } catch (error) {
      toast.error('Failed to add questions to test');
    }
  };

  const togglePoolQuestionSelection = (id) => {
    setSelectedPoolQuestions(prev =>
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  const getTierLabel = (tier) => {
    switch(tier) {
      case 'basic': return 'Basic (Free)';
      case 'comps_ready': return 'Comps Ready ($10)';
      case 'advanced': return 'Advanced ($5/mo)';
      default: return tier;
    }
  };

  return (
    <div className="space-y-6" data-testid="tests-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Testing Center</h2>
        <button
          onClick={() => setShowTestForm(!showTestForm)}
          className="retro-button"
        >
          {showTestForm ? 'Cancel' : '+ New Test'}
        </button>
      </div>

      {/* Create Test Form */}
      {showTestForm && (
        <form onSubmit={handleCreateTest} className="cassette-card p-6 space-y-4">
          <h3 className="text-xl retro-title text-[#2C1810]">Create New Test</h3>
          
          <input
            type="text"
            placeholder="Test Title"
            value={testForm.title}
            onChange={(e) => setTestForm({...testForm, title: e.target.value})}
            required
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423]"
          />
          
          <textarea
            placeholder="Description"
            value={testForm.description}
            onChange={(e) => setTestForm({...testForm, description: e.target.value})}
            required
            rows={3}
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423]"
          />

          <div className="grid grid-cols-2 gap-4">
            <select
              value={testForm.tier}
              onChange={(e) => setTestForm({...testForm, tier: e.target.value})}
              className="px-4 py-2 rounded-lg border-2 border-[#6B4423]"
            >
              <option value="basic">Basic (Free)</option>
              <option value="comps_ready">Comps Ready ($10)</option>
              <option value="advanced">Advanced ($5/mo)</option>
            </select>
            
            <select
              value={testForm.category}
              onChange={(e) => setTestForm({...testForm, category: e.target.value})}
              className="px-4 py-2 rounded-lg border-2 border-[#6B4423]"
            >
              <option value="acupuncture">Acupuncture</option>
              <option value="foundations">Foundations</option>
              <option value="bio">Bio</option>
              <option value="herbs">Herbs</option>
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <input
              type="number"
              placeholder="Questions"
              value={testForm.question_count}
              onChange={(e) => setTestForm({...testForm, question_count: parseInt(e.target.value)})}
              min="1"
              required
              className="px-4 py-2 rounded-lg border-2 border-[#6B4423]"
            />
            
            <input
              type="number"
              placeholder="Pass %"
              value={testForm.passing_score}
              onChange={(e) => setTestForm({...testForm, passing_score: parseInt(e.target.value)})}
              min="0"
              max="100"
              required
              className="px-4 py-2 rounded-lg border-2 border-[#6B4423]"
            />
          </div>

          {['comps_ready', 'advanced'].includes(testForm.tier) && (
            <p className="text-sm text-[#6B4423]">
              ⏱️ Timer: {Math.round(testForm.question_count * 1.25)} seconds (1.25s per question)
            </p>
          )}
          
          <button type="submit" className="retro-button w-full">Create Test</button>
        </form>
      )}

      {/* Test List */}
      {!selectedTest ? (
        <div className="space-y-4">
          {['basic', 'comps_ready', 'advanced'].map(tier => {
            const tierTests = tests.filter(t => t.tier === tier);
            if (tierTests.length === 0) return null;

            return (
              <div key={tier} className="cassette-card p-4">
                <h3 className="text-xl retro-title text-[#2C1810] mb-4">{getTierLabel(tier)}</h3>
                <div className="space-y-2">
                  {tierTests.map(test => (
                    <div key={test.id} className="flex justify-between items-center p-3 bg-white rounded border-2 border-[#6B4423]">
                      <div>
                        <p className="font-bold text-[#2C1810]">{test.title}</p>
                        <p className="text-sm text-[#6B4423]">
                          {test.question_count} questions • {test.passing_score}% to pass
                          {test.time_limit_seconds && ` • ${Math.round(test.time_limit_seconds / 60)} min`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedTest(test);
                            loadQuestions(test.id);
                            setShowQuestionForm(false);
                            setSelectedQuestionIndex(null);
                          }}
                          className="retro-button-small"
                        >
                          Manage
                        </button>
                        <button
                          onClick={() => handleDeleteTest(test.id)}
                          className="retro-button-small bg-red-600 hover:bg-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Question Management */
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setSelectedTest(null);
                  setShowQuestionForm(false);
                }}
                className="retro-button-small"
              >
                ← Back to Tests
              </button>
              <h3 className="text-2xl retro-title">{selectedTest.title}</h3>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowQuestionPoolModal(true);
                  loadQuestionPool();
                }}
                className="retro-button bg-purple-600 hover:bg-purple-700 flex items-center space-x-2"
              >
                <Sparkles className="w-4 h-4" />
                <span>Add AI Questions</span>
              </button>
              <button
                onClick={() => setShowQuestionForm(!showQuestionForm)}
                className="retro-button"
              >
                {showQuestionForm ? 'Cancel' : '+ Add Question'}
              </button>
            </div>
          </div>

          {showQuestionForm && (
            <form onSubmit={handleAddQuestion} className="cassette-card p-6 space-y-4">
              <h4 className="text-xl retro-title text-[#2C1810]">Add Question</h4>
              
              <textarea
                placeholder="Question Text"
                value={questionForm.question_text}
                onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                required
                rows={3}
                className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423]"
              />
              
              <div className="space-y-2">
                <p className="text-sm font-bold text-[#3D2817]">Answer Options (select correct one):</p>
                {questionForm.options.map((option, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <input
                      type="radio"
                      name="correct_answer"
                      checked={questionForm.correct_answer_index === index}
                      onChange={() => setQuestionForm({...questionForm, correct_answer_index: index})}
                      title="Mark as correct answer"
                    />
                    <input
                      type="text"
                      placeholder={`Option ${index + 1}`}
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...questionForm.options];
                        newOptions[index] = e.target.value;
                        setQuestionForm({...questionForm, options: newOptions});
                      }}
                      required
                      className="flex-1 px-3 py-2 rounded border-2 border-[#6B4423]"
                    />
                  </div>
                ))}
                <p className="text-xs text-[#6B4423]">Select radio button for the correct answer</p>
              </div>
              
              <button type="submit" className="retro-button w-full">Add Question</button>
              
              <button
                type="button"
                onClick={() => setShowQuestionForm(false)}
                className="retro-button-small w-full"
              >
                Close Form
              </button>
            </form>
          )}

          {/* Question Navigation Bar */}
          <div className="cassette-card p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl retro-title text-[#2C1810]">
                Questions ({questions.length}/{selectedTest.question_count})
              </h4>
            </div>
            
            {questions.length > 0 ? (
              <>
                {/* Number Navigation Bar */}
                <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b-2 border-[#E8B67A]">
                  {questions.map((q, index) => (
                    <button
                      key={q.id}
                      onClick={() => {
                        setSelectedQuestionIndex(index);
                        setShowQuestionForm(false);
                      }}
                      className={`w-10 h-10 rounded-lg font-bold transition-all ${
                        selectedQuestionIndex === index
                          ? 'bg-[#E8934D] text-white scale-110'
                          : 'bg-[#F4E4C1] text-[#2C1810] hover:bg-[#E8B67A]'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>

                {/* Selected Question Display */}
                {selectedQuestionIndex !== null && questions[selectedQuestionIndex] && (
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border-2 border-[#6B4423]">
                      <div className="flex justify-between items-start mb-3">
                        <h5 className="font-bold text-[#2C1810]">Question {selectedQuestionIndex + 1}</h5>
                        <button
                          onClick={() => handleDeleteQuestion(questions[selectedQuestionIndex].id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-[#3D2817] mb-4">{questions[selectedQuestionIndex].question_text}</p>
                      
                      <div className="space-y-2">
                        {questions[selectedQuestionIndex].options.map((option, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded border-2 ${
                              questions[selectedQuestionIndex].correct_answer_index === idx
                                ? 'border-green-500 bg-green-50'
                                : 'border-[#E8B67A]'
                            }`}
                          >
                            <span className="font-semibold text-[#2C1810]">{String.fromCharCode(65 + idx)}. </span>
                            {option}
                            {questions[selectedQuestionIndex].correct_answer_index === idx && (
                              <span className="ml-2 text-green-600 font-bold">✓ Correct</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditQuestion(questions[selectedQuestionIndex], selectedQuestionIndex)}
                        className="retro-button-small flex-1"
                      >
                        Edit Question
                      </button>
                      {selectedQuestionIndex > 0 && (
                        <button
                          onClick={() => setSelectedQuestionIndex(selectedQuestionIndex - 1)}
                          className="retro-button-small"
                        >
                          ← Previous
                        </button>
                      )}
                      {selectedQuestionIndex < questions.length - 1 && (
                        <button
                          onClick={() => setSelectedQuestionIndex(selectedQuestionIndex + 1)}
                          className="retro-button-small"
                        >
                          Next →
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[#6B4423] text-center py-8">
                No questions added yet. Click "+ Add Question" to get started.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Question Pool Modal */}
      {showQuestionPoolModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="cassette-card p-6 max-w-5xl w-full my-8">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#2C1810]">Add Questions from Pool</h3>
                  <p className="text-sm text-[#6B4423]">Select AI-generated or manually created questions</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowQuestionPoolModal(false);
                  setSelectedPoolQuestions([]);
                }}
                className="text-[#2C1810] hover:text-[#E85D4D]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 p-3 bg-[#F4E4C1] rounded">
              <div>
                <label className="block text-xs font-bold text-[#2C1810] mb-1">Category</label>
                <select
                  value={poolFilters.category}
                  onChange={(e) => {
                    setPoolFilters({ ...poolFilters, category: e.target.value });
                    loadQuestionPool();
                  }}
                  className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810] text-sm"
                >
                  <option value="">All</option>
                  <option value="Acupuncture">Acupuncture</option>
                  <option value="Biomedicine">Biomedicine</option>
                  <option value="Foundations">Foundations</option>
                  <option value="Herbs">Herbs</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#2C1810] mb-1">Level</label>
                <select
                  value={poolFilters.level}
                  onChange={(e) => {
                    setPoolFilters({ ...poolFilters, level: e.target.value });
                    loadQuestionPool();
                  }}
                  className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810] text-sm"
                >
                  <option value="">All</option>
                  <option value="Free">Free</option>
                  <option value="Comps">Comps</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#2C1810] mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2 w-4 h-4 text-[#6B4423]" />
                  <input
                    type="text"
                    value={poolFilters.search}
                    onChange={(e) => setPoolFilters({ ...poolFilters, search: e.target.value })}
                    onKeyPress={(e) => e.key === 'Enter' && loadQuestionPool()}
                    placeholder="Search..."
                    className="w-full pl-8 pr-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810] text-sm"
                  />
                </div>
              </div>
            </div>

            {selectedPoolQuestions.length > 0 && (
              <div className="mb-4 p-3 bg-green-100 border-2 border-green-500 rounded flex justify-between items-center">
                <span className="font-bold text-green-900">{selectedPoolQuestions.length} questions selected</span>
                <button
                  onClick={() => setSelectedPoolQuestions([])}
                  className="text-sm text-green-900 underline"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Questions List */}
            <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
              {questionPool.map((q) => (
                <div
                  key={q.id}
                  className={`p-3 rounded border-2 cursor-pointer transition-colors ${
                    selectedPoolQuestions.includes(q.id)
                      ? 'bg-green-100 border-green-500'
                      : 'bg-white border-[#6B4423] hover:bg-[#F4E4C1]'
                  }`}
                  onClick={() => togglePoolQuestionSelection(q.id)}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedPoolQuestions.includes(q.id)}
                      onChange={() => togglePoolQuestionSelection(q.id)}
                      className="mt-1 w-4 h-4"
                    />
                    <div className="flex-1">
                      <p className="font-bold text-[#2C1810] text-sm mb-1">{q.question_text}</p>
                      <div className="flex gap-1 flex-wrap mb-2">
                        <span className="px-2 py-0.5 bg-[#E8934D] text-white rounded text-xs">
                          {q.category}
                        </span>
                        <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">
                          {q.level}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-500 text-white rounded text-xs">
                          {q.source}
                        </span>
                        {q.tags?.map(tag => (
                          <span key={tag} className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs">
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {q.options.map((opt, idx) => (
                          <div
                            key={idx}
                            className={`p-1 rounded text-xs ${
                              idx === q.correct_answer_index
                                ? 'bg-green-200 font-bold'
                                : 'bg-gray-100'
                            }`}
                          >
                            {String.fromCharCode(65 + idx)}. {opt}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {questionPool.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No questions found. Use the Testing Center to create questions!
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleAddPoolQuestionsToTest}
                disabled={selectedPoolQuestions.length === 0}
                className="retro-button flex-1"
              >
                Add {selectedPoolQuestions.length} Question{selectedPoolQuestions.length !== 1 ? 's' : ''} to Test
              </button>
              <button
                onClick={() => {
                  setShowQuestionPoolModal(false);
                  setSelectedPoolQuestions([]);
                }}
                className="retro-button bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Study Buddy Manager
function StudyBuddyManager() {
  const [settings, setSettings] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grantAccessEmail, setGrantAccessEmail] = useState('');
  const [granting, setGranting] = useState(false);
  
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, subsRes] = await Promise.all([
        axios.get(`${API}/admin/study-buddy-settings`),
        axios.get(`${API}/admin/study-buddy-subscribers`)
      ]);
      
      setSettings(settingsRes.data);
      setSubscribers(subsRes.data);
    } catch (error) {
      console.error('Failed to load Study Buddy data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/study-buddy-settings`, settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!grantAccessEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setGranting(true);
    try {
      await axios.post(`${API}/admin/study-buddy-grant-access`, null, {
        params: { email: grantAccessEmail }
      });
      toast.success(`Premium access granted to ${grantAccessEmail}`);
      setGrantAccessEmail('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setGranting(false);
    }
  };

  const handleRevokeAccess = async (userId) => {
    if (!confirm('Are you sure you want to revoke premium access for this user?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/study-buddy-revoke-access/${userId}`);
      toast.success('Access revoked successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-bold retro-title text-[#2C1810]">Study Buddy Management</h2>

      {/* Subscription Settings */}
      <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
        <h3 className="text-2xl font-bold text-[#2C1810] mb-6">Subscription Settings</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block font-bold text-gray-700 mb-2">
              Monthly Subscription Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings?.monthly_price || 10.00}
              onChange={(e) => setSettings({ ...settings, monthly_price: parseFloat(e.target.value) })}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
            />
            <p className="text-sm text-gray-600 mt-1">Current price: ${settings?.monthly_price || 10.00}/month</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block font-bold text-gray-700 mb-2">
                Max Courses (Free Tier)
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings?.max_courses_free || 1}
                onChange={(e) => setSettings({ ...settings, max_courses_free: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
              />
            </div>

            <div>
              <label className="block font-bold text-gray-700 mb-2">
                Max Courses (Premium)
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings?.max_courses_premium || 10}
                onChange={(e) => setSettings({ ...settings, max_courses_premium: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
              />
            </div>
          </div>

          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Grant Free Access */}
      <div className="bg-white border-4 border-green-500 rounded-lg shadow-lg p-6">
        <h3 className="text-2xl font-bold text-[#2C1810] mb-4">Grant Free Premium Access</h3>
        <p className="text-gray-700 mb-4">
          Give specific users free access to all {settings?.max_courses_premium || 10} college courses without requiring payment.
        </p>
        
        <div className="flex gap-4">
          <input
            type="email"
            value={grantAccessEmail}
            onChange={(e) => setGrantAccessEmail(e.target.value)}
            placeholder="Enter user email address"
            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none text-gray-900 bg-white"
          />
          <button
            onClick={handleGrantAccess}
            disabled={granting}
            className="px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {granting ? 'Granting...' : 'Grant Access'}
          </button>
        </div>
      </div>

      {/* Subscribers List */}
      <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
        <h3 className="text-2xl font-bold text-[#2C1810] mb-6">Active Users</h3>
        
        {subscribers.length === 0 ? (
          <p className="text-gray-600 text-center py-8">No subscribers yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 px-4 font-bold text-gray-700">User Email</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-700">Type</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-700">Since</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub) => (
                  <tr key={sub.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900">{sub.user_email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        sub.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        sub.free_access_granted_by_admin ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {sub.free_access_granted_by_admin ? 'Free (Admin)' : 'Paid'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {new Date(sub.subscription_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      {sub.free_access_granted_by_admin && (
                        <button
                          onClick={() => handleRevokeAccess(sub.user_id)}
                          className="px-4 py-2 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600 transition-colors text-sm"
                        >
                          Revoke Access
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <h4 className="text-lg font-bold mb-2">Total Subscribers</h4>
          <p className="text-4xl font-bold">{subscribers.length}</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <h4 className="text-lg font-bold mb-2">Paid Subscribers</h4>
          <p className="text-4xl font-bold">
            {subscribers.filter(s => !s.free_access_granted_by_admin).length}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <h4 className="text-lg font-bold mb-2">Free Access (Admin)</h4>
          <p className="text-4xl font-bold">
            {subscribers.filter(s => s.free_access_granted_by_admin).length}
          </p>
        </div>
      </div>
    </div>
  );
}

// Users Manager
function UsersManager() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollCourseId, setEnrollCourseId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [savingUser, setSavingUser] = useState(false);

  useEffect(() => {
    loadUsers();
    loadCourses();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    }
  };

  const loadUserEnrollments = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/enrollments`, {
        withCredentials: true
      });
      setEnrollments(response.data);
      setSelectedUser(users.find(u => u.id === userId));
    } catch (error) {
      toast.error('Failed to load enrollments');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(
        `${API}/admin/users/${resetUserId}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Password reset successfully! User must login again.');
      setShowPasswordReset(false);
      setNewPassword('');
      setResetUserId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    setSavingUser(true);
    try {
      await axios.put(
        `${API}/admin/users/${editingUser.id}`,
        {
          name: editForm.name,
          email: editForm.email
        },
        { withCredentials: true }
      );
      toast.success('User updated successfully!');
      setShowEditUser(false);
      setEditingUser(null);
      setEditForm({ name: '', email: '' });
      loadUsers(); // Reload users list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`⚠️ DELETE USER?\n\nAre you sure you want to permanently delete:\n\nName: ${user.name}\nEmail: ${user.email}\n\nThis action CANNOT be undone and will:\n- Delete the user account\n- Remove all enrollments\n- Clear all progress data\n- Delete all certificates\n\nType "DELETE" to confirm.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        withCredentials: true
      });
      toast.success(`User "${user.name}" deleted successfully`);
      loadUsers(); // Reload users list
      // If we're viewing this user's details, go back to list
      if (selectedUser?.id === user.id) {
        setSelectedUser(null);
        setEnrollments([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleAdmin = async (user) => {
    const currentRole = user.role || 'visitor';
    const isAdmin = currentRole === 'admin';
    const action = isAdmin ? 'revoke' : 'grant';
    
    if (!window.confirm(`${action.toUpperCase()} ADMIN ACCESS?\n\nUser: ${user.name}\nEmail: ${user.email}\n\nAre you sure you want to ${action} admin access for this user?`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/users/${user.id}/toggle-admin`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      loadUsers(); // Reload users list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle admin access');
    }
  };


  const handleManualEnroll = async (e) => {
    e.preventDefault();
    if (!selectedUser || !enrollCourseId) return;

    try {
      await axios.post(
        `${API}/admin/enroll-user`,
        null,
        {
          params: { user_id: selectedUser.id, course_id: enrollCourseId },
          withCredentials: true
        }
      );
      toast.success('User enrolled successfully!');
      setShowEnrollForm(false);
      setEnrollCourseId('');
      loadUserEnrollments(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll user');
    }
  };

  const handleUnenroll = async (enrollmentId) => {
    if (!window.confirm('Remove this enrollment?')) return;

    try {
      await axios.delete(`${API}/admin/enrollments/${enrollmentId}`, {
        withCredentials: true
      });
      toast.success('Enrollment removed');
      loadUserEnrollments(selectedUser.id);
    } catch (error) {
      toast.error('Failed to remove enrollment');
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="users-manager">
      <h2 className="text-3xl retro-title">Users Management</h2>

      {!selectedUser ? (
        <>
          {/* Search */}
          <div className="cassette-card p-4">
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423]"
            />
          </div>

          {/* Users List */}
          <div className="cassette-card p-6">
            <h3 className="text-xl retro-title text-[#2C1810] mb-4">All Users ({filteredUsers.length})</h3>
            <div className="space-y-2">
              {filteredUsers.map((user) => (
                <div key={user.id} className="flex justify-between items-center p-4 bg-white rounded border-2 border-[#6B4423]">
                  <div>
                    <p className="font-bold text-[#2C1810]">{user.name}</p>
                    <p className="text-sm text-[#6B4423]">{user.email}</p>
                    <p className="text-xs text-[#6B4423]">Role: {user.role || 'visitor'}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleToggleAdmin(user)}
                      className={`retro-button-small ${
                        (user.role === 'admin') 
                          ? 'bg-orange-600 hover:bg-orange-700' 
                          : 'bg-[#4D9E7C] hover:bg-[#3D7E5C]'
                      } text-white flex items-center gap-1`}
                      title={user.role === 'admin' ? 'Revoke admin access' : 'Grant admin access'}
                    >
                      <Settings className="w-4 h-4" />
                      {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingUser(user);
                        setEditForm({ name: user.name, email: user.email });
                        setShowEditUser(true);
                      }}
                      className="retro-button-small bg-[#4D9E7C] hover:bg-[#E8934D]"
                    >
                      Edit Info
                    </button>
                    <button
                      onClick={() => loadUserEnrollments(user.id)}
                      className="retro-button-small"
                    >
                      View Enrollments
                    </button>
                    <button
                      onClick={() => {
                        setResetUserId(user.id);
                        setShowPasswordReset(true);
                      }}
                      className="retro-button-small bg-[#E85D4D] hover:bg-[#E8934D]"
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user)}
                      className="retro-button-small bg-red-700 hover:bg-red-800 text-white flex items-center gap-1"
                      title="Delete user permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        /* User Details */
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setSelectedUser(null);
                setEnrollments([]);
              }}
              className="retro-button-small"
            >
              ← Back to Users
            </button>
            <div>
              <h3 className="text-2xl retro-title">{selectedUser.name}</h3>
              <p className="text-[#6B4423]">{selectedUser.email}</p>
            </div>
          </div>

          {/* Enrollments */}
          <div className="cassette-card p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl retro-title text-[#2C1810]">
                Enrollments ({enrollments.length})
              </h4>
              <button
                onClick={() => setShowEnrollForm(!showEnrollForm)}
                className="retro-button-small"
              >
                + Manual Enroll
              </button>
            </div>

            {showEnrollForm && (
              <form onSubmit={handleManualEnroll} className="mb-4 p-4 bg-[#F4E4C1] rounded space-y-2">
                <select
                  value={enrollCourseId}
                  onChange={(e) => setEnrollCourseId(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded border-2 border-[#6B4423]"
                >
                  <option value="">Select a course...</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title} ({course.is_free ? 'FREE' : `$${course.price}`})
                    </option>
                  ))}
                </select>
                <button type="submit" className="retro-button w-full">Enroll User</button>
              </form>
            )}

            <div className="space-y-2">
              {enrollments.length === 0 ? (
                <p className="text-[#6B4423] text-center py-4">No enrollments yet</p>
              ) : (
                enrollments.map((enrollment) => (
                  <div key={enrollment.id} className="flex justify-between items-center p-3 bg-white rounded border-2 border-[#6B4423]">
                    <div>
                      <p className="font-bold text-[#2C1810]">{enrollment.course_title}</p>
                      <p className="text-sm text-[#6B4423]">
                        Status: {enrollment.payment_status} | 
                        Price: ${enrollment.course_price || 0}
                      </p>
                    </div>
                    <button
                      onClick={() => handleUnenroll(enrollment.id)}
                      className="retro-button-small bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cassette-card max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title">Reset User Password</h3>
              <button
                onClick={() => {
                  setShowPasswordReset(false);
                  setNewPassword('');
                }}
                className="text-[#6B4423] hover:text-[#E85D4D] text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-[#6B4423] font-bold mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                  minLength={6}
                  placeholder="Enter new password"
                />
                <p className="text-sm text-[#6B4423] mt-1">Minimum 6 characters. User will be logged out.</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={resettingPassword}
                  className="retro-button flex-1"
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordReset(false);
                    setNewPassword('');
                  }}
                  className="retro-button bg-[#6B4423] hover:bg-[#3D2817] flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cassette-card max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title">Edit User Info</h3>
              <button
                onClick={() => {
                  setShowEditUser(false);
                  setEditingUser(null);
                  setEditForm({ name: '', email: '' });
                }}
                className="text-[#6B4423] hover:text-[#E85D4D] text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-[#6B4423] font-bold mb-2">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                  placeholder="User's full name"
                />
              </div>

              <div>
                <label className="block text-[#6B4423] font-bold mb-2">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                  placeholder="user@example.com"
                />
                <p className="text-sm text-[#6B4423] mt-1">Email must be unique</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={savingUser}
                  className="retro-button flex-1"
                >
                  {savingUser ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditUser(false);
                    setEditingUser(null);
                    setEditForm({ name: '', email: '' });
                  }}
                  className="retro-button bg-[#6B4423] hover:bg-[#3D2817] flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Hero Images Manager
function HeroImagesManager() {
  const [heroImages, setHeroImages] = useState({});
  const [uploading, setUploading] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [showUrlInput, setShowUrlInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [currentPageForUnsplash, setCurrentPageForUnsplash] = useState(null);
  
  const pages = [
    { id: 'home', label: 'Home Page - Hero' },
    { id: 'home_specializations', label: 'Home Page - Specializations Background' },
    { id: 'patient_videos', label: 'Patient Videos' },
    { id: 'podcasts', label: 'Podcasts' },
    { id: 'internal_practices', label: 'Internal Practices' },
    { id: 'study_buddy', label: 'Study Buddy' },
    { id: 'about', label: 'About' },
    { id: 'courses', label: 'Courses' },
    { id: 'testing_center', label: 'Testing Center' },
    { id: 'student_resources', label: 'Student Resources' },
    { id: 'books', label: 'Books' },
    { id: 'student_blog', label: 'Student Blog' }
  ];

  useEffect(() => {
    loadHeroImages();
  }, []);

  const loadHeroImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hero-images`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      const images = {};
      const status = {};
      response.data.forEach(img => {
        images[img.page_name] = img.image_url;
        status[img.page_name] = 'saved';
      });
      setHeroImages(images);
      setSaveStatus(status);
      toast.success('Hero images loaded successfully');
    } catch (error) {
      console.error('Failed to load hero images:', error);
      toast.error('Failed to load hero images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (pageName, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image file is too large. Maximum size is 5MB.');
      return;
    }

    setUploading(prev => ({ ...prev, [pageName]: true }));
    setSaveStatus(prev => ({ ...prev, [pageName]: 'uploading' }));
    
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('page_name', pageName);

    try {
      const response = await axios.post(
        `${API}/admin/hero-images/upload?page_name=${pageName}`,
        uploadData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000 // 30 second timeout
        }
      );
      
      // Verify the upload by fetching the image URL
      const verifyResponse = await axios.get(`${API}/hero-images/${pageName}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (verifyResponse.data && verifyResponse.data.image_url) {
        setHeroImages(prev => ({ ...prev, [pageName]: verifyResponse.data.image_url }));
        setSaveStatus(prev => ({ ...prev, [pageName]: 'saved' }));
        setLastSaved(new Date().toLocaleTimeString());
        toast.success(`✓ Hero image for ${pageName.replace(/_/g, ' ')} saved successfully!`);
      } else {
        throw new Error('Upload verification failed');
      }
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      setSaveStatus(prev => ({ ...prev, [pageName]: 'error' }));
      toast.error(`Failed to upload: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [pageName]: false }));
    }
  };

  const handleUrlUpdate = async (pageName) => {
    const url = imageUrls[pageName];
    if (!url) {
      toast.error('Please enter an image URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    setUploading(prev => ({ ...prev, [pageName]: true }));
    setSaveStatus(prev => ({ ...prev, [pageName]: 'uploading' }));

    try {
      await axios.put(
        `${API}/admin/hero-images/${pageName}?image_url=${encodeURIComponent(url)}`,
        {},
        { 
          withCredentials: true,
          timeout: 15000
        }
      );
      
      // Verify the save
      const verifyResponse = await axios.get(`${API}/hero-images/${pageName}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (verifyResponse.data && verifyResponse.data.image_url) {
        setHeroImages(prev => ({ ...prev, [pageName]: verifyResponse.data.image_url }));
        setImageUrls(prev => ({ ...prev, [pageName]: '' }));
        setShowUrlInput(prev => ({ ...prev, [pageName]: false }));
        setSaveStatus(prev => ({ ...prev, [pageName]: 'saved' }));
        setLastSaved(new Date().toLocaleTimeString());
        toast.success(`✓ Hero image for ${pageName.replace(/_/g, ' ')} saved successfully!`);
      } else {
        throw new Error('Save verification failed');
      }
    } catch (error) {
      console.error('URL update error:', error);
      setSaveStatus(prev => ({ ...prev, [pageName]: 'error' }));
      toast.error(`Failed to update: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [pageName]: false }));
    }
  };

  const handleDelete = async (pageName) => {
    if (!window.confirm(`Delete hero image for ${pageName.replace(/_/g, ' ')}?\n\nThis will remove the image from the page.`)) return;

    setUploading(prev => ({ ...prev, [pageName]: true }));
    setSaveStatus(prev => ({ ...prev, [pageName]: 'deleting' }));

    try {
      await axios.delete(`${API}/admin/hero-images/${pageName}`, {
        withCredentials: true
      });
      
      // Verify deletion
      const verifyResponse = await axios.get(`${API}/hero-images/${pageName}`);
      if (!verifyResponse.data) {
        setHeroImages(prev => {
          const updated = { ...prev };
          delete updated[pageName];
          return updated;
        });
        setSaveStatus(prev => {
          const updated = { ...prev };
          delete updated[pageName];
          return updated;
        });
        setLastSaved(new Date().toLocaleTimeString());
        toast.success(`✓ Hero image for ${pageName.replace(/_/g, ' ')} deleted`);
      } else {
        throw new Error('Deletion verification failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setSaveStatus(prev => ({ ...prev, [pageName]: 'error' }));
      toast.error(`Failed to delete: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [pageName]: false }));
    }
  };

  const handleUnsplashSelect = async (imageData) => {
    if (!currentPageForUnsplash) return;
    
    const pageName = currentPageForUnsplash;
    setUploading(prev => ({ ...prev, [pageName]: true }));
    setSaveStatus(prev => ({ ...prev, [pageName]: 'uploading' }));

    try {
      await axios.put(
        `${API}/admin/hero-images/${pageName}?image_url=${encodeURIComponent(imageData.url)}`,
        {},
        { withCredentials: true }
      );
      
      // Verify the update
      const verifyResponse = await axios.get(`${API}/hero-images/${pageName}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (verifyResponse.data && verifyResponse.data.image_url) {
        setHeroImages(prev => ({ ...prev, [pageName]: verifyResponse.data.image_url }));
        setSaveStatus(prev => ({ ...prev, [pageName]: 'saved' }));
        setLastSaved(new Date().toLocaleTimeString());
        toast.success(`✓ Image by ${imageData.photographer} set for ${pageName.replace(/_/g, ' ')}`);
      }
    } catch (error) {
      console.error('Unsplash update error:', error);
      setSaveStatus(prev => ({ ...prev, [pageName]: 'error' }));
      toast.error(`Failed to update: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [pageName]: false }));
      setCurrentPageForUnsplash(null);
    }
  };

  const openUnsplashPicker = (pageName) => {
    setCurrentPageForUnsplash(pageName);
    setShowUnsplashPicker(true);
  };

  const handleRefreshAll = async () => {
    toast.info('Refreshing all hero images...');
    await loadHeroImages();
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    const url = imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}${imageUrl}`;
    // Add timestamp to force reload
    return `${url}?t=${Date.now()}`;
  };

  const getSaveStatusBadge = (pageName) => {
    const status = saveStatus[pageName];
    if (status === 'saved') {
      return <span className="px-2 py-1 bg-green-600 text-white text-xs rounded font-bold">✓ SAVED</span>;
    } else if (status === 'uploading' || status === 'deleting') {
      return <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded font-bold animate-pulse">SAVING...</span>;
    } else if (status === 'error') {
      return <span className="px-2 py-1 bg-red-600 text-white text-xs rounded font-bold">ERROR</span>;
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="hero-images-manager">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl retro-title">Hero Images</h2>
          <p className="text-[#3D2817]">Manage hero background images for each page. Upload files or use external URLs.</p>
          {lastSaved && (
            <p className="text-sm text-green-700 font-semibold mt-2">
              ✓ Last saved: {lastSaved}
            </p>
          )}
        </div>
        <button
          onClick={handleRefreshAll}
          disabled={loading}
          className="retro-button-small flex items-center gap-2"
          title="Refresh and reload all hero images"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh All
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-[#E8934D]"></div>
          <p className="text-[#6B4423] mt-4 font-semibold">Loading hero images...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pages.map(page => {
            const currentImage = heroImages[page.id];
            const imageUrl = getImageUrl(currentImage);
            const isUploading = uploading[page.id];
            
            return (
              <div key={page.id} className="cassette-card p-6 space-y-4">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1">
                    <h3 className="text-xl retro-title text-[#3D2817]">{page.label}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {currentImage ? (
                        <span className="text-xs text-green-700 font-semibold">✓ Image Set</span>
                      ) : (
                        <span className="text-xs text-orange-700">No image set</span>
                      )}
                      {getSaveStatusBadge(page.id)}
                    </div>
                  </div>
                  {currentImage && !isUploading && (
                    <button
                      onClick={() => handleDelete(page.id)}
                      className="retro-button-small bg-red-600 hover:bg-red-700 text-white flex items-center gap-1 flex-shrink-0"
                      title="Remove current image"
                      data-testid={`hero-delete-${page.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </button>
                  )}
                </div>
                
                {/* Current Image Preview - Larger and Clearer */}
                {imageUrl && (
                  <div className="relative border-4 border-[#E8B67A] rounded-lg overflow-hidden bg-[#3D2817]">
                    <img
                      src={imageUrl}
                      alt={`${page.label} hero`}
                      className="w-full h-64 object-cover"
                      data-testid={`hero-preview-${page.id}`}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage not found%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    {currentImage.startsWith('http') && (
                      <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">
                        External URL
                      </div>
                    )}
                  </div>
                )}

                {isUploading && (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E8934D]"></div>
                    <p className="text-[#3D2817] text-sm mt-2 font-semibold">Uploading...</p>
                  </div>
                )}

                {!isUploading && (
                  <div>
                    <button
                      onClick={() => openUnsplashPicker(page.id)}
                      className="retro-button bg-blue-600 text-white hover:bg-blue-700 w-full flex items-center justify-center gap-2"
                      data-testid={`hero-unsplash-${page.id}`}
                    >
                      <ImageIcon className="w-5 h-5" />
                      {currentImage ? 'Replace Image from Unsplash' : 'Browse Images from Unsplash'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Unsplash Image Picker Modal */}
      <UnsplashImagePicker
        isOpen={showUnsplashPicker}
        onClose={() => {
          setShowUnsplashPicker(false);
          setCurrentPageForUnsplash(null);
        }}
        onSelect={handleUnsplashSelect}
      />
    </div>
  );
}

// Settings Manager

// Simple Course AI Settings Component
function SimpleCourseAISettings() {
  const [aiModel, setAiModel] = useState('gpt-5');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/simple-course/ai-settings`, {
        withCredentials: true
      });
      setAiModel(response.data.ai_model || 'gpt-5');
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/simple-course/ai-settings`,
        {
          id: 'simple_course_ai',
          ai_model: aiModel
        },
        { withCredentials: true }
      );
      toast.success('AI model settings saved!');
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cassette-card p-6">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cassette-card p-6 space-y-4">
      <h3 className="text-xl retro-title text-[#3D2817] mb-4">Simple Course AI Model</h3>
      <div className="bg-blue-100 border-2 border-blue-600 rounded p-4">
        <p className="text-blue-800 font-semibold mb-2">🤖 AI Model Selection</p>
        <p className="text-sm text-blue-700">
          Select which AI model to use for Simple Course generation (chapter materials, study guides, exams, etc.)
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#6B4423] mb-2">
          Select AI Model
        </label>
        <select
          value={aiModel}
          onChange={(e) => setAiModel(e.target.value)}
          className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
        >
          <option value="gpt-5">OpenAI GPT-5 (Default)</option>
          <option value="claude-sonnet-4">Anthropic Claude Sonnet 4</option>
          <option value="gemini-2.5-pro">Google Gemini 2.5 Pro</option>
        </select>
        <p className="text-xs text-[#6B4423] mt-2">
          💡 This setting only affects Simple Course Generator. Other AI features use their own models.
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="retro-button"
      >
        {saving ? 'Saving...' : 'Save AI Model Settings'}
      </button>
    </div>
  );
}


// Simple Blog AI Settings Component
function SimpleBlogAISettings() {
  const [aiModel, setAiModel] = useState('gpt-5');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/simple-blog/ai-settings`, {
        withCredentials: true
      });
      setAiModel(response.data.ai_model || 'gpt-5');
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/simple-blog/ai-settings`,
        {
          id: 'simple_blog_ai',
          ai_model: aiModel
        },
        { withCredentials: true }
      );
      toast.success('AI model settings saved!');
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cassette-card p-6">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cassette-card p-6 space-y-4">
      <h3 className="text-xl retro-title text-[#3D2817] mb-4">Simple Blog AI Model</h3>
      <div className="bg-green-100 border-2 border-green-600 rounded p-4">
        <p className="text-green-800 font-semibold mb-2">✍️ AI Model Selection</p>
        <p className="text-sm text-green-700">
          Select which AI model to use for Simple Blog generation (title, content, refinement, etc.)
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#6B4423] mb-2">
          Select AI Model
        </label>
        <select
          value={aiModel}
          onChange={(e) => setAiModel(e.target.value)}
          className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
        >
          <option value="gpt-5">OpenAI GPT-5 (Default)</option>
          <option value="claude-sonnet-4">Anthropic Claude Sonnet 4</option>
          <option value="gemini-2.5-pro">Google Gemini 2.5 Pro</option>
        </select>
        <p className="text-xs text-[#6B4423] mt-2">
          💡 This setting only affects Simple Blog Generator. Other AI features use their own models.
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="retro-button"
      >
        {saving ? 'Saving...' : 'Save AI Model Settings'}
      </button>
    </div>
  );
}

// Simple Lesson AI Settings Component
function SimpleLessonAISettings() {
  const [aiModel, setAiModel] = useState('gpt-5');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/simple-lesson/ai-settings`, {
        withCredentials: true
      });
      setAiModel(response.data.ai_model || 'gpt-5');
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/simple-lesson/ai-settings`,
        {
          id: 'simple_lesson_ai',
          ai_model: aiModel
        },
        { withCredentials: true }
      );
      toast.success('AI model settings saved!');
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cassette-card p-6">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cassette-card p-6 space-y-4">
      <h3 className="text-xl retro-title text-[#3D2817] mb-4">Simple Lesson AI Model</h3>
      <div className="bg-emerald-100 border-2 border-emerald-600 rounded p-4">
        <p className="text-emerald-800 font-semibold mb-2">🎓 AI Model Selection</p>
        <p className="text-sm text-emerald-700">
          Select which AI model to use for Simple Lesson generation (chapter materials, study guides, exams, etc.)
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#6B4423] mb-2">
          Select AI Model
        </label>
        <select
          value={aiModel}
          onChange={(e) => setAiModel(e.target.value)}
          className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
        >
          <option value="gpt-5">OpenAI GPT-5 (Default)</option>
          <option value="claude-sonnet-4">Anthropic Claude Sonnet 4</option>
          <option value="gemini-2.5-pro">Google Gemini 2.5 Pro</option>
        </select>
        <p className="text-xs text-[#6B4423] mt-2">
          💡 This setting only affects Simple Lesson Generator. Other AI features use their own models.
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="retro-button"
      >
        {saving ? 'Saving...' : 'Save AI Model Settings'}
      </button>
    </div>
  );
}

function SettingsManager() {
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [coffeeLink, setCoffeeLink] = useState('');
  const [coffeeEnabled, setCoffeeEnabled] = useState(true);
  const [savingCoffee, setSavingCoffee] = useState(false);
  const [contactButtonText, setContactButtonText] = useState('Contact Me');
  const [contactButtonUrl, setContactButtonUrl] = useState('');
  const [contactButtonEnabled, setContactButtonEnabled] = useState(true);
  const [savingContactButton, setSavingContactButton] = useState(false);

  useEffect(() => {
    loadConfig();
    loadCoffeeLink();
    loadContactButton();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/site-config`);
      setLogoUrl(response.data.logo_url);
    } catch (error) {
      console.error('Failed to load config');
    }
  };

  const loadCoffeeLink = async () => {
    try {
      const response = await axios.get(`${API}/settings/coffee-link`);
      setCoffeeLink(response.data.coffee_link || '');
      setCoffeeEnabled(response.data.enabled !== false);
    } catch (error) {
      console.error('Failed to load coffee link');
    }
  };

  const loadContactButton = async () => {
    try {
      const response = await axios.get(`${API}/settings/contact-button`);
      setContactButtonText(response.data.button_text || 'Contact Me');
      setContactButtonUrl(response.data.button_url || '');
      setContactButtonEnabled(response.data.enabled !== false);
    } catch (error) {
      console.error('Failed to load contact button');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-logo`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoUrl(response.data.logo_url);
      toast.success('Logo updated! Refresh to see changes.');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveCoffeeLink = async () => {
    setSavingCoffee(true);
    try {
      await axios.put(`${API}/admin/settings/coffee-link`, {
        id: 'coffee_link',
        coffee_link: '',  // No longer used with Stripe integration
        enabled: coffeeEnabled
      }, {
        withCredentials: true
      });
      toast.success('Donation settings saved!');
    } catch (error) {
      toast.error('Failed to save donation settings');
    } finally {
      setSavingCoffee(false);
    }
  };


  const handleSaveContactButton = async () => {
    setSavingContactButton(true);
    try {
      await axios.put(`${API}/admin/settings/contact-button`, {
        id: 'contact_button',
        button_text: contactButtonText,
        button_url: contactButtonUrl,
        enabled: contactButtonEnabled
      }, {
        withCredentials: true
      });
      toast.success('Contact button saved!');
    } catch (error) {
      toast.error('Failed to save contact button');
    } finally {
      setSavingContactButton(false);
    }
  };


  return (
    <div className="space-y-6" data-testid="settings-manager">
      <h2 className="text-3xl retro-title">Site Settings</h2>
      
      {/* Logo Settings */}
      <div className="cassette-card p-6 space-y-6">
        <div>
          <h3 className="text-xl retro-title text-[#3D2817] mb-4">Logo</h3>
          {logoUrl && (
            <div className="mb-4">
              <img src={`${BACKEND_URL}${logoUrl}`} alt="Current logo" className="h-20 w-auto" data-testid="current-logo" />
            </div>
          )}
          <label className="block">
            <span className="text-[#3D2817] mb-2 block">Upload New Logo:</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploading}
              className="w-full"
              data-testid="logo-upload-input"
            />
          </label>
          {uploading && <p className="text-[#3D2817] mt-2">Uploading...</p>}
        </div>
      </div>

      {/* Donation Button Settings */}
      <div className="cassette-card p-6 space-y-4">
        <h3 className="text-xl retro-title text-[#3D2817] mb-4">Donation Button (Stripe)</h3>
        <div className="bg-[#E8934D]/10 p-4 rounded border-2 border-[#E8934D]">
          <p className="text-[#2C1810] font-semibold mb-2">✨ Stripe Integration Active</p>
          <p className="text-sm text-[#6B4423]">
            The "Buy Me a Coffee!" button now uses Stripe for secure donations. Visitors can choose from preset amounts ($5, $10, $20, $50) or enter a custom amount.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={coffeeEnabled}
            onChange={(e) => setCoffeeEnabled(e.target.checked)}
            className="w-5 h-5"
            id="coffee-enabled"
          />
          <label htmlFor="coffee-enabled" className="text-sm font-semibold text-[#6B4423]">
            Enable "Buy me a Coffee!" donation button
          </label>
        </div>

        <button
          onClick={handleSaveCoffeeLink}
          disabled={savingCoffee}
          className="retro-button"
        >
          {savingCoffee ? 'Saving...' : 'Save Donation Settings'}
        </button>
      </div>

      {/* AI Model Settings for Simple Course */}
      <SimpleCourseAISettings />

      {/* AI Model Settings for Simple Blog */}
      <SimpleBlogAISettings />

      {/* AI Model Settings for Simple Lesson */}
      <SimpleLessonAISettings />

      {/* Contact Button Settings */}
      <div className="cassette-card p-6 space-y-4">
        <h3 className="text-xl retro-title text-[#3D2817] mb-4">Find Me At - Contact Button</h3>
        <div>
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">
            Button Text
          </label>
          <input
            type="text"
            value={contactButtonText}
            onChange={(e) => setContactButtonText(e.target.value)}
            placeholder="Contact Me"
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">
            Button Link (Phone or Website)
          </label>
          <input
            type="text"
            value={contactButtonUrl}
            onChange={(e) => setContactButtonUrl(e.target.value)}
            placeholder="tel:+1234567890 or https://yourwebsite.com"
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
          />
          <p className="text-xs text-[#6B4423] mt-2">
            💡 For phone: use format <code className="bg-[#E8934D]/20 px-1">tel:+1234567890</code><br/>
            💡 For website: use full URL <code className="bg-[#E8934D]/20 px-1">https://example.com</code><br/>
            💡 Leave empty to hide the button.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={contactButtonEnabled}
            onChange={(e) => setContactButtonEnabled(e.target.checked)}
            className="w-5 h-5"
            id="contact-button-enabled"
          />
          <label htmlFor="contact-button-enabled" className="text-sm font-semibold text-[#6B4423]">
            Enable contact button
          </label>
        </div>

        <button
          onClick={handleSaveContactButton}
          disabled={savingContactButton}
          className="retro-button"
        >
          {savingContactButton ? 'Saving...' : 'Save Contact Button'}
        </button>
      </div>

      {/* Internal Practices Settings */}
      <InternalPracticesSettingsManager />
    </div>
  );
}

// Internal Practices Settings Manager
function InternalPracticesSettingsManager() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [currentService, setCurrentService] = useState(null); // 'private_session', 'one_on_one', or 'free_group_study'

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/internal-practices-settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load Internal Practices settings');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/internal-practices-settings`, settings, {
        withCredentials: true
      });
      toast.success('Internal Practices settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsplashSelect = (imageData) => {
    if (currentService && settings) {
      setSettings({
        ...settings,
        [currentService]: {
          ...settings[currentService],
          image_url: imageData.url
        }
      });
      toast.success('Image selected! Remember to click Save.');
    }
    setShowUnsplashPicker(false);
    setCurrentService(null);
  };

  const openUnsplashPicker = (serviceName) => {
    setCurrentService(serviceName);
    setShowUnsplashPicker(true);
  };

  if (!settings) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="cassette-card p-6 space-y-6">
      <h3 className="text-2xl retro-title text-[#3D2817] mb-4">Internal Practices Page Settings</h3>
      <p className="text-sm text-[#6B4423] mb-6">
        Configure the three service cards on the Internal Practices page. Control descriptions, button text, and clickable status.
      </p>

      {/* Private Session Settings */}
      <div className="border-2 border-[#6B4423] rounded-lg p-4 bg-[#F4E4C1]/30">
        <h4 className="text-lg font-bold text-[#3D2817] mb-3">📅 Private Session</h4>
        
        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Background Image</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openUnsplashPicker('private_session')}
              className="retro-button-small flex items-center gap-2"
            >
              <ImageIcon className="w-4 h-4" />
              {settings.private_session.image_url ? 'Change Image' : 'Select Image from Unsplash'}
            </button>
            {settings.private_session.image_url && (
              <button
                type="button"
                onClick={() => setSettings({
                  ...settings,
                  private_session: { ...settings.private_session, image_url: '' }
                })}
                className="text-red-600 hover:text-red-800 text-sm font-semibold"
              >
                Remove
              </button>
            )}
          </div>
          {settings.private_session.image_url && (
            <img 
              src={settings.private_session.image_url} 
              alt="Private Session" 
              className="mt-2 w-full h-32 object-cover rounded border-2 border-[#6B4423]"
            />
          )}
        </div>
        
        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
          <textarea
            value={settings.private_session.description}
            onChange={(e) => setSettings({
              ...settings,
              private_session: { ...settings.private_session, description: e.target.value }
            })}
            rows={2}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            placeholder="Service description"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Button Text</label>
          <input
            type="text"
            value={settings.private_session.button_text}
            onChange={(e) => setSettings({
              ...settings,
              private_session: { ...settings.private_session, button_text: e.target.value }
            })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            placeholder="Button text"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={settings.private_session.is_clickable}
            onChange={(e) => setSettings({
              ...settings,
              private_session: { ...settings.private_session, is_clickable: e.target.checked }
            })}
            className="w-5 h-5"
            id="private-clickable"
          />
          <label htmlFor="private-clickable" className="text-sm font-semibold text-[#6B4423]">
            Make button clickable (opens booking modal)
          </label>
        </div>
      </div>

      {/* One-on-One Settings */}
      <div className="border-2 border-[#6B4423] rounded-lg p-4 bg-[#F4E4C1]/30">
        <h4 className="text-lg font-bold text-[#3D2817] mb-3">👤 One-on-One Consultation</h4>
        
        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Background Image</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openUnsplashPicker('one_on_one')}
              className="retro-button-small flex items-center gap-2"
            >
              <ImageIcon className="w-4 h-4" />
              {settings.one_on_one.image_url ? 'Change Image' : 'Select Image from Unsplash'}
            </button>
            {settings.one_on_one.image_url && (
              <button
                type="button"
                onClick={() => setSettings({
                  ...settings,
                  one_on_one: { ...settings.one_on_one, image_url: '' }
                })}
                className="text-red-600 hover:text-red-800 text-sm font-semibold"
              >
                Remove
              </button>
            )}
          </div>
          {settings.one_on_one.image_url && (
            <img 
              src={settings.one_on_one.image_url} 
              alt="One-on-One" 
              className="mt-2 w-full h-32 object-cover rounded border-2 border-[#6B4423]"
            />
          )}
        </div>
        
        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
          <textarea
            value={settings.one_on_one.description}
            onChange={(e) => setSettings({
              ...settings,
              one_on_one: { ...settings.one_on_one, description: e.target.value }
            })}
            rows={2}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            placeholder="Service description"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Button Text</label>
          <input
            type="text"
            value={settings.one_on_one.button_text}
            onChange={(e) => setSettings({
              ...settings,
              one_on_one: { ...settings.one_on_one, button_text: e.target.value }
            })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            placeholder="Button text"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={settings.one_on_one.is_clickable}
            onChange={(e) => setSettings({
              ...settings,
              one_on_one: { ...settings.one_on_one, is_clickable: e.target.checked }
            })}
            className="w-5 h-5"
            id="one-on-one-clickable"
          />
          <label htmlFor="one-on-one-clickable" className="text-sm font-semibold text-[#6B4423]">
            Make button clickable (opens one-on-one booking modal)
          </label>
        </div>
      </div>

      {/* Free Group Study Settings */}
      <div className="border-2 border-[#6B4423] rounded-lg p-4 bg-[#F4E4C1]/30">
        <h4 className="text-lg font-bold text-[#3D2817] mb-3">👥 Free Group Study</h4>
        
        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Background Image</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openUnsplashPicker('free_group_study')}
              className="retro-button-small flex items-center gap-2"
            >
              <ImageIcon className="w-4 h-4" />
              {settings.free_group_study.image_url ? 'Change Image' : 'Select Image from Unsplash'}
            </button>
            {settings.free_group_study.image_url && (
              <button
                type="button"
                onClick={() => setSettings({
                  ...settings,
                  free_group_study: { ...settings.free_group_study, image_url: '' }
                })}
                className="text-red-600 hover:text-red-800 text-sm font-semibold"
              >
                Remove
              </button>
            )}
          </div>
          {settings.free_group_study.image_url && (
            <img 
              src={settings.free_group_study.image_url} 
              alt="Free Group Study" 
              className="mt-2 w-full h-32 object-cover rounded border-2 border-[#6B4423]"
            />
          )}
        </div>
        
        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
          <textarea
            value={settings.free_group_study.description}
            onChange={(e) => setSettings({
              ...settings,
              free_group_study: { ...settings.free_group_study, description: e.target.value }
            })}
            rows={2}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            placeholder="Service description"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Button Text</label>
          <input
            type="text"
            value={settings.free_group_study.button_text}
            onChange={(e) => setSettings({
              ...settings,
              free_group_study: { ...settings.free_group_study, button_text: e.target.value }
            })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            placeholder="Button text"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#6B4423] mb-2">Zoom Link</label>
          <input
            type="url"
            value={settings.free_group_study.zoom_link}
            onChange={(e) => setSettings({
              ...settings,
              free_group_study: { ...settings.free_group_study, zoom_link: e.target.value }
            })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            placeholder="https://zoom.us/j/..."
          />
          <p className="text-xs text-[#6B4423] mt-1">💡 Enter the full Zoom meeting link</p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={settings.free_group_study.is_clickable}
            onChange={(e) => setSettings({
              ...settings,
              free_group_study: { ...settings.free_group_study, is_clickable: e.target.checked }
            })}
            className="w-5 h-5"
            id="group-study-clickable"
          />
          <label htmlFor="group-study-clickable" className="text-sm font-semibold text-[#6B4423]">
            Make button clickable (opens Zoom link in new tab)
          </label>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="retro-button w-full"
      >
        {saving ? 'Saving...' : 'Save Internal Practices Settings'}
      </button>

      {/* Unsplash Image Picker Modal */}
      {showUnsplashPicker && (
        <UnsplashImagePicker
          isOpen={showUnsplashPicker}
          onClose={() => {
            setShowUnsplashPicker(false);
            setCurrentService(null);
          }}
          onSelect={handleUnsplashSelect}
        />
      )}
    </div>
  );
}

// Quotes Manager
function QuotesManager() {
  const [quotes, setQuotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    page_name: 'home',
    quote_text: '',
    author: '',
    position: 1
  });

  const pages = [
    { value: 'home', label: 'Home Page', maxQuotes: 3 },
    { value: 'about', label: 'About', maxQuotes: 1 },
    { value: 'podcasts', label: 'Podcasts', maxQuotes: 1 },
    { value: 'courses', label: 'Courses', maxQuotes: 1 },
    { value: 'testing_center', label: 'Testing Center', maxQuotes: 1 },
    { value: 'student_blog', label: 'Student Blog', maxQuotes: 1 },
    { value: 'patient_videos', label: 'Patient Videos', maxQuotes: 1 },
    { value: 'internal_practices', label: 'Internal Practices', maxQuotes: 1 },
    { value: 'study_buddy', label: 'Study Buddy', maxQuotes: 2 },
    { value: 'books', label: 'Books', maxQuotes: 1 },
    { value: 'student_resources', label: 'Student Resources', maxQuotes: 1 }
  ];

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quotes`, {
        withCredentials: true
      });
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingQuote) {
        await axios.put(`${API}/admin/quotes/${editingQuote.id}`, quoteForm, {
          withCredentials: true
        });
        toast.success('Quote updated!');
      } else {
        await axios.post(`${API}/admin/quotes`, quoteForm, {
          withCredentials: true
        });
        toast.success('Quote created!');
      }
      setQuoteForm({ page_name: 'home', quote_text: '', author: '', position: 1 });
      setShowForm(false);
      setEditingQuote(null);
      loadQuotes();
    } catch (error) {
      toast.error('Failed to save quote');
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setQuoteForm({
      page_name: quote.page_name,
      quote_text: quote.quote_text,
      author: quote.author || '',
      position: quote.position
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quote?')) return;

    try {
      await axios.delete(`${API}/admin/quotes/${id}`, {
        withCredentials: true
      });
      toast.success('Quote deleted');
      loadQuotes();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const groupedQuotes = quotes.reduce((acc, quote) => {
    if (!acc[quote.page_name]) {
      acc[quote.page_name] = [];
    }
    acc[quote.page_name].push(quote);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Page Quotes</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingQuote(null);
            setQuoteForm({ page_name: 'home', quote_text: '', author: '', position: 1 });
          }}
          className="retro-button"
        >
          {showForm ? 'Cancel' : '+ Add Quote'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="cassette-card p-6 space-y-4">
          <h3 className="text-xl retro-title text-[#2C1810]">
            {editingQuote ? 'Edit Quote' : 'Add New Quote'}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-2">Page</label>
              <select
                value={quoteForm.page_name}
                onChange={(e) => setQuoteForm({...quoteForm, page_name: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810]"
              >
                {pages.map(page => (
                  <option key={page.value} value={page.value}>{page.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-2">Position</label>
              <input
                type="number"
                min="1"
                max="3"
                value={quoteForm.position}
                onChange={(e) => setQuoteForm({...quoteForm, position: parseInt(e.target.value)})}
                className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810]"
              />
              <p className="text-xs text-[#6B4423] mt-1">Home page: 1, 2, or 3 | Other pages: 1 only</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-[#2C1810] mb-2">Quote Text *</label>
            <textarea
              placeholder="Enter the quote..."
              value={quoteForm.quote_text}
              onChange={(e) => setQuoteForm({...quoteForm, quote_text: e.target.value})}
              required
              rows={3}
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-[#2C1810] mb-2">Author (optional)</label>
            <input
              type="text"
              placeholder="- Author Name"
              value={quoteForm.author}
              onChange={(e) => setQuoteForm({...quoteForm, author: e.target.value})}
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
            />
          </div>

          <button type="submit" className="retro-button w-full">
            {editingQuote ? 'Update Quote' : 'Create Quote'}
          </button>
        </form>
      )}

      <div className="space-y-6">
        {pages.map(page => (
          <div key={page.value} className="cassette-card p-6">
            <h3 className="text-xl retro-title text-[#2C1810] mb-4">{page.label}</h3>
            {groupedQuotes[page.value] && groupedQuotes[page.value].length > 0 ? (
              <div className="space-y-3">
                {groupedQuotes[page.value].map(quote => (
                  <div key={quote.id} className="p-4 bg-white rounded-lg border-2 border-[#6B4423]">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-[#2C1810] italic text-lg mb-2">"{quote.quote_text}"</p>
                        {quote.author && (
                          <p className="text-[#6B4423] text-sm">— {quote.author}</p>
                        )}
                        <p className="text-xs text-[#6B4423] mt-2">Position: {quote.position}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEdit(quote)}
                          className="text-[#E8934D] hover:text-[#E85D4D]"
                        >
                          <Save className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(quote.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#6B4423] text-center py-4">No quotes added yet</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Page Icons Manager
function PageIconsManager() {
  const [icons, setIcons] = useState({});
  const [iconForm, setIconForm] = useState({
    page_name: 'podcasts',
    icon_url: ''
  });
  const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'file'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const pages = [
    { value: 'podcasts', label: 'Podcasts' },
    { value: 'courses', label: 'Courses' },
    { value: 'testing_center', label: 'Testing Center' },
    { value: 'patient_videos', label: 'Patient Videos' },
    { value: 'books', label: 'Books' },
    { value: 'internal_practices', label: 'Internal Practices' },
    { value: 'study_buddy', label: 'Study Buddy' },
    { value: 'student_blog', label: 'Student Blog' },
    { value: 'about', label: 'About' },
    { value: 'student_resources', label: 'Student Resources' }
  ];

  useEffect(() => {
    loadIcons();
  }, []);

  const loadIcons = async () => {
    try {
      const response = await axios.get(`${API}/admin/page-icons`, {
        withCredentials: true
      });
      const iconsObj = response.data.reduce((acc, icon) => {
        acc[icon.page_name] = icon.icon_url;
        return acc;
      }, {});
      setIcons(iconsObj);
    } catch (error) {
      toast.error('Failed to load icons');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploadMethod === 'url') {
      if (!iconForm.icon_url) {
        toast.error('Please enter an icon URL');
        return;
      }

      try {
        await axios.post(`${API}/admin/page-icons`, iconForm, {
          withCredentials: true
        });
        toast.success('Icon updated!');
        setIconForm({ page_name: 'podcasts', icon_url: '' });
        loadIcons();
      } catch (error) {
        toast.error('Failed to update icon');
      }
    } else {
      // File upload
      if (!selectedFile) {
        toast.error('Please select a file');
        return;
      }

      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const response = await axios.post(
          `${API}/admin/page-icons/upload?page_name=${iconForm.page_name}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
          }
        );
        toast.success('Icon uploaded successfully!');
        setSelectedFile(null);
        loadIcons();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload icon');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (SVG, PNG, JPG, GIF, or WebP)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDelete = async (pageName) => {
    if (!window.confirm(`Remove icon for ${pageName}?`)) return;

    try {
      await axios.delete(`${API}/admin/page-icons/${pageName}`, {
        withCredentials: true
      });
      toast.success('Icon removed');
      loadIcons();
    } catch (error) {
      toast.error('Failed to remove icon');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl retro-title">Page Icons</h2>
      
      <div className="cassette-card p-6">
        <div className="bg-[#E8934D]/20 border-2 border-[#E8934D] rounded-lg p-4 mb-6">
          <h3 className="font-bold text-[#2C1810] mb-2">📌 How to Add Icons:</h3>
          <ul className="text-sm text-[#2C1810] space-y-2 list-disc list-inside">
            <li><strong>Upload File:</strong> Upload icons directly from your computer (SVG, PNG, JPG, GIF, WebP)</li>
            <li><strong>Use URL:</strong> Paste a direct link to an image hosted elsewhere</li>
            <li><strong>Best Quality:</strong> Square images (300x300px+) with transparent backgrounds</li>
          </ul>
        </div>

        {/* Method Selector */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setUploadMethod('file')}
            className={`retro-button-small ${uploadMethod === 'file' ? 'bg-[#E8934D] text-white' : ''}`}
          >
            📤 Upload File
          </button>
          <button
            type="button"
            onClick={() => setUploadMethod('url')}
            className={`retro-button-small ${uploadMethod === 'url' ? 'bg-[#E8934D] text-white' : ''}`}
          >
            🔗 Use URL
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-[#2C1810] mb-2">Page</label>
            <select
              value={iconForm.page_name}
              onChange={(e) => setIconForm({...iconForm, page_name: e.target.value})}
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810]"
            >
              {pages.map(page => (
                <option key={page.value} value={page.value}>{page.label}</option>
              ))}
            </select>
          </div>

          {/* URL Input */}
          {uploadMethod === 'url' && (
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-2">Icon URL *</label>
              <input
                type="url"
                placeholder="https://example.com/icon.svg"
                value={iconForm.icon_url}
                onChange={(e) => setIconForm({...iconForm, icon_url: e.target.value})}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
              />
              <p className="text-xs text-[#6B4423] mt-1">Direct link to SVG, PNG, JPG, or other image format</p>
              
              {iconForm.icon_url && (
                <div className="mt-4 p-4 bg-[#F4E4C1] rounded-lg">
                  <p className="text-sm font-bold text-[#2C1810] mb-2">Preview:</p>
                  <img 
                    src={iconForm.icon_url} 
                    alt="Icon preview" 
                    className="w-32 h-32 object-contain mx-auto"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <p className="text-center text-red-600 text-sm hidden">❌ Invalid image URL</p>
                </div>
              )}
            </div>
          )}

          {/* File Upload */}
          {uploadMethod === 'file' && (
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-2">Upload Icon File *</label>
              <input
                type="file"
                accept=".svg,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileChange}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810]"
              />
              <p className="text-xs text-[#6B4423] mt-1">Accepted formats: SVG, PNG, JPG, GIF, WebP</p>
              
              {selectedFile && (
                <div className="mt-4 p-4 bg-[#F4E4C1] rounded-lg">
                  <p className="text-sm text-[#2C1810]">
                    <span className="font-bold">Selected:</span> {selectedFile.name}
                  </p>
                  <p className="text-xs text-[#6B4423]">
                    Size: {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              )}
            </div>
          )}

          <button 
            type="submit" 
            disabled={uploading}
            className="retro-button w-full"
          >
            {uploading ? 'Uploading...' : uploadMethod === 'file' ? 'Upload Icon' : 'Update Icon'}
          </button>
        </form>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl retro-title">Current Icons</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map(page => (
            <div key={page.value} className="cassette-card p-4">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-bold text-[#2C1810]">{page.label}</h4>
                {icons[page.value] && (
                  <button
                    onClick={() => handleDelete(page.value)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {icons[page.value] ? (
                <div className="bg-white p-4 rounded border-2 border-[#6B4423]">
                  <img 
                    src={icons[page.value]} 
                    alt={`${page.label} icon`}
                    className="w-24 h-24 object-contain mx-auto"
                  />
                  <p className="text-xs text-[#6B4423] text-center mt-2 break-all">
                    {icons[page.value].length > 40 
                      ? icons[page.value].substring(0, 40) + '...' 
                      : icons[page.value]
                    }
                  </p>
                </div>
              ) : (
                <div className="bg-gray-100 p-4 rounded border-2 border-gray-300 text-center">
                  <p className="text-gray-500 text-sm">No icon set</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Social Media Manager
function SocialMediaManager() {
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    youtube: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSocialLinks();
  }, []);

  const loadSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-links`);
      setSocialLinks({
        facebook: response.data.facebook || '',
        instagram: response.data.instagram || '',
        youtube: response.data.youtube || ''
      });
    } catch (error) {
      toast.error('Failed to load social links');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/admin/social-links`, socialLinks, {
        withCredentials: true
      });
      toast.success('Social media links updated!');
    } catch (error) {
      toast.error('Failed to update social links');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl retro-title">Social Media Links</h2>
      
      <form onSubmit={handleSave} className="cassette-card p-6 space-y-6">
        <p className="text-[#6B4423]">
          Add your social media profile URLs. These will appear as icons in the footer.
        </p>

        <div>
          <label className="flex items-center gap-2 mb-2">
            <Facebook className="w-5 h-5 text-[#1877F2]" />
            <span className="font-bold text-[#2C1810]">Facebook</span>
          </label>
          <input
            type="url"
            placeholder="https://facebook.com/yourpage"
            value={socialLinks.facebook}
            onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 mb-2">
            <Instagram className="w-5 h-5 text-[#E4405F]" />
            <span className="font-bold text-[#2C1810]">Instagram</span>
          </label>
          <input
            type="url"
            placeholder="https://instagram.com/yourusername"
            value={socialLinks.instagram}
            onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 mb-2">
            <Youtube className="w-5 h-5 text-[#FF0000]" />
            <span className="font-bold text-[#2C1810]">YouTube</span>
          </label>
          <input
            type="url"
            placeholder="https://youtube.com/@yourchannel"
            value={socialLinks.youtube}
            onChange={(e) => setSocialLinks({...socialLinks, youtube: e.target.value})}
            className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] bg-white text-[#2C1810] placeholder-gray-500"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="retro-button w-full"
        >
          {saving ? 'Saving...' : 'Save Social Media Links'}
        </button>

        <div className="mt-6 p-4 bg-[#F4E4C1] rounded-lg">
          <h4 className="font-bold text-[#2C1810] mb-2">Preview:</h4>
          <div className="flex gap-6 justify-center">
            {socialLinks.facebook && (
              <div className="text-center">
                <Facebook className="w-8 h-8 text-[#E8934D] mx-auto" />
                <p className="text-xs text-[#6B4423] mt-1">Facebook</p>
              </div>
            )}
            {socialLinks.instagram && (
              <div className="text-center">
                <Instagram className="w-8 h-8 text-[#E8934D] mx-auto" />
                <p className="text-xs text-[#6B4423] mt-1">Instagram</p>
              </div>
            )}
            {socialLinks.youtube && (
              <div className="text-center">
                <Youtube className="w-8 h-8 text-[#E8934D] mx-auto" />
                <p className="text-xs text-[#6B4423] mt-1">YouTube</p>
              </div>
            )}
          </div>
          {!socialLinks.facebook && !socialLinks.instagram && !socialLinks.youtube && (
            <p className="text-center text-[#6B4423] text-sm">No social media links added yet</p>
          )}
        </div>
      </form>
    </div>
  );
}

// Mailing List Manager
function MailingListManager() {
  const [subscribers, setSubscribers] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [showNewsletterForm, setShowNewsletterForm] = useState(false);
  const [newsletterForm, setNewsletterForm] = useState({
    subject: '',
    preview_text: '',
    html_content: ''
  });
  const [sending, setSending] = useState(false);
  const [view, setView] = useState('subscribers'); // subscribers, newsletters, compose

  useEffect(() => {
    loadSubscribers();
    loadNewsletters();
  }, []);

  const loadSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/mailing-list/subscribers`, {
        withCredentials: true
      });
      setSubscribers(response.data);
    } catch (error) {
      toast.error('Failed to load subscribers');
    }
  };

  const loadNewsletters = async () => {
    try {
      const response = await axios.get(`${API}/admin/mailing-list/newsletters`, {
        withCredentials: true
      });
      setNewsletters(response.data);
    } catch (error) {
      toast.error('Failed to load newsletters');
    }
  };

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;

    try {
      await axios.delete(`${API}/admin/mailing-list/subscribers/${id}`, {
        withCredentials: true
      });
      toast.success('Subscriber deleted');
      loadSubscribers();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };

  const handleSendNewsletter = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send newsletter to ${subscribers.filter(s => s.status === 'active').length} subscribers?`)) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/mailing-list/send-newsletter`, newsletterForm, {
        withCredentials: true
      });
      toast.success(response.data.message);
      setNewsletterForm({ subject: '', preview_text: '', html_content: '' });
      setShowNewsletterForm(false);
      setView('newsletters');
      loadNewsletters();
    } catch (error) {
      toast.error('Failed to send newsletter');
    } finally {
      setSending(false);
    }
  };

  const activeSubscribers = subscribers.filter(s => s.status === 'active');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Mailing List</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setView('subscribers')}
            className={`retro-button-small ${view === 'subscribers' ? 'bg-[#E8934D]' : ''}`}
          >
            Subscribers ({activeSubscribers.length})
          </button>
          <button
            onClick={() => setView('newsletters')}
            className={`retro-button-small ${view === 'newsletters' ? 'bg-[#E8934D]' : ''}`}
          >
            History ({newsletters.length})
          </button>
          <button
            onClick={() => {
              setView('compose');
              setShowNewsletterForm(true);
            }}
            className="retro-button"
          >
            + Compose Newsletter
          </button>
        </div>
      </div>

      {/* Subscribers View */}
      {view === 'subscribers' && (
        <div className="cassette-card p-6">
          <h3 className="text-xl retro-title text-[#2C1810] mb-4">
            Subscribers ({activeSubscribers.length} active, {subscribers.filter(s => s.status === 'unsubscribed').length} unsubscribed)
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {subscribers.length === 0 ? (
              <p className="text-[#6B4423] text-center py-8">No subscribers yet</p>
            ) : (
              subscribers.map(sub => (
                <div key={sub.id} className="flex justify-between items-center p-3 bg-white rounded border-2 border-[#6B4423]">
                  <div>
                    <p className="font-bold text-[#2C1810]">{sub.email}</p>
                    {sub.name && <p className="text-sm text-[#6B4423]">{sub.name}</p>}
                    <p className="text-xs text-[#6B4423]">
                      {sub.status === 'active' ? '✓ Active' : '✗ Unsubscribed'} • 
                      Joined {new Date(sub.subscribed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteSubscriber(sub.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Newsletter History View */}
      {view === 'newsletters' && (
        <div className="cassette-card p-6">
          <h3 className="text-xl retro-title text-[#2C1810] mb-4">Newsletter History</h3>
          <div className="space-y-4">
            {newsletters.length === 0 ? (
              <p className="text-[#6B4423] text-center py-8">No newsletters sent yet</p>
            ) : (
              newsletters.map(newsletter => (
                <div key={newsletter.id} className="p-4 bg-white rounded border-2 border-[#6B4423]">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-bold text-[#2C1810] text-lg">{newsletter.subject}</h4>
                      {newsletter.preview_text && (
                        <p className="text-sm text-[#6B4423] mt-1">{newsletter.preview_text}</p>
                      )}
                      <p className="text-xs text-[#6B4423] mt-2">
                        Sent to {newsletter.recipient_count} subscribers • 
                        {new Date(newsletter.sent_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm text-[#E8934D] font-semibold">
                      View Content
                    </summary>
                    <div 
                      className="mt-2 p-3 bg-gray-50 rounded border border-[#E8B67A] max-h-60 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: newsletter.html_content }}
                    />
                  </details>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Compose Newsletter View */}
      {view === 'compose' && showNewsletterForm && (
        <form onSubmit={handleSendNewsletter} className="cassette-card p-6 space-y-4">
          <h3 className="text-xl retro-title text-[#2C1810]">Compose Newsletter</h3>
          
          <div>
            <label className="block text-sm font-bold text-[#3D2817] mb-2">Subject Line *</label>
            <input
              type="text"
              placeholder="Newsletter subject"
              value={newsletterForm.subject}
              onChange={(e) => setNewsletterForm({...newsletterForm, subject: e.target.value})}
              required
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423]"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-[#3D2817] mb-2">Preview Text (optional)</label>
            <input
              type="text"
              placeholder="Short preview that appears in email clients"
              value={newsletterForm.preview_text}
              onChange={(e) => setNewsletterForm({...newsletterForm, preview_text: e.target.value})}
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423]"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-[#3D2817] mb-2">Email Content (HTML) *</label>
            <textarea
              placeholder="Enter your newsletter content (HTML supported)"
              value={newsletterForm.html_content}
              onChange={(e) => setNewsletterForm({...newsletterForm, html_content: e.target.value})}
              required
              rows={12}
              className="w-full px-4 py-2 rounded-lg border-2 border-[#6B4423] font-mono text-sm"
            />
            <p className="text-xs text-[#6B4423] mt-1">
              Tip: Use HTML tags like &lt;h1&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;a href=""&gt;, etc.
            </p>
          </div>

          {/* Preview */}
          {newsletterForm.html_content && (
            <div>
              <h4 className="text-sm font-bold text-[#3D2817] mb-2">Preview:</h4>
              <div 
                className="p-4 bg-white rounded border-2 border-[#6B4423] max-h-60 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: newsletterForm.html_content }}
              />
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={sending}
              className="retro-button flex-1"
            >
              {sending ? 'Sending...' : `Send to ${activeSubscribers.length} Subscribers`}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowNewsletterForm(false);
                setView('subscribers');
              }}
              className="retro-button-small"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


// Test Subscriptions Manager
function TestSubscriptionsManager() {
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTier, setSelectedTier] = useState('advanced');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUsers();
    loadSubscriptions();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-subscriptions`, {
        withCredentials: true
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
    }
  };

  const grantAccess = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/grant-test-access`,
        { user_id: selectedUser, tier: selectedTier },
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      toast.success('Access granted!');
      loadSubscriptions();
      setSelectedUser('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setLoading(false);
    }
  };

  const revokeAccess = async (subscriptionId) => {
    if (!window.confirm('Revoke this user\'s test access?')) return;

    try {
      await axios.delete(`${API}/admin/test-subscriptions/${subscriptionId}`, {
        withCredentials: true
      });
      toast.success('Access revoked');
      loadSubscriptions();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const getSubscriptionsByTier = (tier) => {
    return subscriptions.filter(sub => sub.tier === tier && sub.status === 'active');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl retro-title text-[#2C1810]">Test Access Management</h2>
      
      {/* Grant Access Form */}
      <div className="cassette-card p-6">
        <h3 className="text-xl font-bold text-[#2C1810] mb-4">Grant Test Access</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">User</label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            >
              <option value="">Select User...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.email} {user.name ? `(${user.name})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier</label>
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            >
              <option value="comps_ready">Comps Ready</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={grantAccess}
              disabled={loading}
              className="retro-button w-full"
            >
              {loading ? 'Granting...' : 'Grant Access'}
            </button>
          </div>
        </div>
        <p className="text-sm text-[#6B4423] mt-3">
          💡 Basic tier is free for all users. Grant access to Comps Ready or Advanced tiers here.
        </p>
      </div>

      {/* Active Subscriptions by Tier */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Comps Ready */}
        <div className="cassette-card p-6">
          <h3 className="text-xl font-bold text-[#2C1810] mb-4">Comps Ready Access</h3>
          {getSubscriptionsByTier('comps_ready').length === 0 ? (
            <p className="text-[#6B4423]">No users with Comps Ready access</p>
          ) : (
            <div className="space-y-3">
              {getSubscriptionsByTier('comps_ready').map(sub => {
                const user = users.find(u => u.id === sub.user_id);
                return (
                  <div key={sub.id} className="flex items-center justify-between bg-[#F4E4C1] p-3 rounded">
                    <div>
                      <p className="font-bold text-[#2C1810]">{user?.email || 'Unknown'}</p>
                      {user?.name && typeof user.name === 'string' && (
                        <p className="text-xs text-[#6B4423]">{user.name}</p>
                      )}
                    </div>
                    <button
                      onClick={() => revokeAccess(sub.id)}
                      className="retro-button-small bg-red-500 hover:bg-red-600"
                    >
                      Revoke
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Advanced */}
        <div className="cassette-card p-6">
          <h3 className="text-xl font-bold text-[#2C1810] mb-4">Advanced Access</h3>
          {getSubscriptionsByTier('advanced').length === 0 ? (
            <p className="text-[#6B4423]">No users with Advanced access</p>
          ) : (
            <div className="space-y-3">
              {getSubscriptionsByTier('advanced').map(sub => {
                const user = users.find(u => u.id === sub.user_id);
                return (
                  <div key={sub.id} className="flex items-center justify-between bg-[#F4E4C1] p-3 rounded">
                    <div>
                      <p className="font-bold text-[#2C1810]">{user?.email || 'Unknown'}</p>
                      {user?.name && typeof user.name === 'string' && (
                        <p className="text-xs text-[#6B4423]">{user.name}</p>
                      )}
                    </div>
                    <button
                      onClick={() => revokeAccess(sub.id)}
                      className="retro-button-small bg-red-500 hover:bg-red-600"
                    >
                      Revoke
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// Classes Manager
function ClassesManager() {
  const [classes, setClasses] = useState([]);
  const [editingClass, setEditingClass] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image_url: '',
    tier: 'basic',
    category: 'general',
    price: 0,
    is_free: true,
    membership_required: false,
    status: 'published'
  });

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const response = await axios.get(`${API}/admin/classes`, { withCredentials: true });
      setClasses(response.data);
    } catch (error) {
      toast.error('Failed to load classes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClass) {
        await axios.put(
          `${API}/admin/classes/${editingClass.id}`,
          { ...editingClass, ...formData },
          { withCredentials: true }
        );
        toast.success('Class updated!');
      } else {
        await axios.post(`${API}/admin/classes`, formData, { withCredentials: true });
        toast.success('Class created!');
      }
      resetForm();
      loadClasses();
    } catch (error) {
      toast.error('Failed to save class');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this class and all its lessons?')) return;
    try {
      await axios.delete(`${API}/admin/classes/${id}`, { withCredentials: true });
      toast.success('Class deleted!');
      loadClasses();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      title: cls.title,
      description: cls.description,
      cover_image_url: cls.cover_image_url || '',
      tier: cls.tier || 'basic',
      category: cls.category || 'general',
      price: cls.price || 0,
      is_free: cls.is_free !== undefined ? cls.is_free : true,
      membership_required: cls.membership_required || false,
      status: cls.status || 'published'
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      cover_image_url: '',
      tier: 'basic',
      category: 'general',
      price: 0,
      is_free: true,
      membership_required: false,
      status: 'published'
    });
    setEditingClass(null);
    setShowForm(false);
  };

  const handleUnsplashSelect = (imageData) => {
    setFormData(prev => ({
      ...prev,
      cover_image_url: imageData.url
    }));
    setShowUnsplashPicker(false);
    toast.success(`Image by ${imageData.photographer} selected!`);
  };

  return (
    <div className="cassette-card p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl retro-title">Classes Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="retro-button"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          {showForm ? 'Cancel' : 'Add Class'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 bg-[#F4E4C1] rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-bold mb-2 text-[#2C1810]">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full p-2 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 text-[#2C1810]">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full p-2 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="coming_soon">Coming Soon</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-2 text-[#2C1810]">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={4}
              className="w-full p-2 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-bold mb-2 text-[#2C1810]">Tier</label>
              <select
                value={formData.tier}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                className="w-full p-2 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              >
                <option value="basic">Basic</option>
                <option value="comps_ready">Comp's Ready</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 text-[#2C1810]">Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full p-2 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 text-[#2C1810]">Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                className="w-full p-2 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="flex items-center gap-2 text-[#2C1810]">
                <input
                  type="checkbox"
                  checked={formData.is_free}
                  onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="font-bold">Free Class</span>
              </label>
            </div>

            <div>
              <label className="flex items-center gap-2 text-[#2C1810]">
                <input
                  type="checkbox"
                  checked={formData.membership_required}
                  onChange={(e) => setFormData({ ...formData, membership_required: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="font-bold">Requires Membership</span>
              </label>
            </div>
          </div>

          {/* Cover Image */}
          <div className="mb-4">
            <label className="block text-sm font-bold mb-2 text-[#2C1810]">Cover Image URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.cover_image_url}
                onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                placeholder="Enter image URL or select from Unsplash"
                className="flex-1 p-2 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              />
              <button
                type="button"
                onClick={() => setShowUnsplashPicker(true)}
                className="retro-button-small"
              >
                <ImageIcon className="w-5 h-5" />
              </button>
            </div>
            {formData.cover_image_url && (
              <img src={formData.cover_image_url} alt="Preview" className="mt-2 w-32 h-32 object-cover rounded" />
            )}
          </div>

          <div className="flex gap-4">
            <button type="submit" className="retro-button">
              <Save className="w-5 h-5 inline mr-2" />
              {editingClass ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={resetForm} className="retro-button bg-gray-500">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Unsplash Picker */}
      {showUnsplashPicker && (
        <UnsplashImagePicker
          isOpen={showUnsplashPicker}
          onClose={() => setShowUnsplashPicker(false)}
          onSelect={handleUnsplashSelect}
        />
      )}

      {/* Classes List */}
      <div className="space-y-4">
        {classes.length === 0 ? (
          <p className="text-center text-gray-600 py-8">No classes yet. Create your first class!</p>
        ) : (
          classes.map((cls) => (
            <div key={cls.id} className="bg-white border-4 border-[#8B5A3C] rounded-lg p-6">
              <div className="flex items-start gap-4">
                {/* Cover Image */}
                {cls.cover_image_url && (
                  <img 
                    src={cls.cover_image_url} 
                    alt={cls.title}
                    className="w-24 h-24 object-cover rounded"
                  />
                )}

                {/* Class Info */}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-[#2C1810] mb-2">{cls.title}</h3>
                  <p className="text-gray-700 mb-2">{cls.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {cls.tier}
                    </span>
                    <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {cls.category}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      cls.status === 'published' ? 'bg-green-100 text-green-700' :
                      cls.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {cls.status}
                    </span>
                    {cls.is_free ? (
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                        FREE
                      </span>
                    ) : (
                      <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-semibold">
                        ${cls.price}
                      </span>
                    )}
                    {cls.membership_required && (
                      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                        Membership Required
                      </span>
                    )}
                  </div>

                  <div className="text-sm text-gray-600">
                    <BookOpen className="w-4 h-4 inline mr-1" />
                    {cls.lesson_ids?.length || 0} Lessons
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(cls)}
                    className="retro-button-small"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(cls.id)}
                    className="retro-button-small bg-red-500 hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

